import React, {
  ComponentProps,
  FC,
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import MobileNavToggle from './MobileNavToggle';
import PlatformStatsUserLookup from './PlatformStatsUserLookup';
import PlatformStatsUserExperience from './PlatformStatsUserExperience';
import PlatformStatsOpsWorkers from './PlatformStatsOpsWorkers';
import PlatformStatsPayments from './PlatformStatsPayments';
import PlatformStatsCampaignCodes from './PlatformStatsCampaignCodes';
import PlatformStatsFeatureFlags from './PlatformStatsFeatureFlags';
import UserTypesPanel from './UserTypesPanel';
import OverviewDashboard from './OverviewDashboard';
import ActivationDashboard from './ActivationDashboard';
import AcquisitionDashboard from './AcquisitionDashboard';
import ScalingDashboard from './ScalingDashboard';
import GrowthDashboard from './GrowthDashboard';
import BrowserLadderDashboard from './BrowserLadderDashboard';
import WebWorkMixDashboard from './webWorkMix/WebWorkMixDashboard';
import OpsCapacityDashboard from './ops-capacity/OpsCapacityDashboard';
import LaunchesDashboard from './LaunchesDashboard';
import PromotionsDashboard from './PromotionsDashboard';
import DailyCostChart, {
  DailyCostDimension,
  DailyCostFilters,
} from './DailyCostChart';
import ModelBreakdownTable from './ModelBreakdownTable';
import PlatformStatsCaptchaSolver from './PlatformStatsCaptchaSolver';
import { BrowserSessionsResponse } from '@dreamai/types';
import { formatLabel, formatUSD } from './platformStatsChartUtils';
import './platform-stats.scss';
import './activation-dashboard.scss';
import {
  TAB_GROUPS,
  groupOf,
  platformStatsPath,
  resolveTab,
  type Tab,
} from './platformStatsNav';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModelUsageRow {
  model: string;
  calls: number;
  pct: number;
  cost: number;
}

interface WeeklyCohortRow {
  cohortWeek: string;
  cohortSize: number;
  retention: number[]; // W0..W12
}

interface ReturnCohort {
  cohortSize: number;
  daily: number[]; // D0..D30
  dailyCohortSizes: number[]; // eligible users per day offset
  weeklyCohorts: WeeklyCohortRow[];
}

interface StatsResponse {
  success: boolean;
  days: string[];
  totalOnboardedUsers: number;
  series: {
    tasks: number[];
    standups: number[];
    calls: number[];
    messages: number[];
    messagesWhatsapp: number[];
    messagesInApp: number[];
    dau: number[];
    mau: number[];
    onboardedUsers: number[];
    standupEnabledUsers: number[];
    sessions: number[];
    sessionsActiveUsers: number[];
  };
  returnCohort: ReturnCohort;
  llmCosts: {
    totalCost: number[];
    avgCostPerCall: number[];
    avgCostPerUser: number[];
    modelUsage: ModelUsageRow[][];
    triggerCosts?: {
      month: Array<{ trigger: string; cost: number }>;
      week: Array<{ trigger: string; cost: number }>;
      day: Array<{ trigger: string; cost: number }>;
    };
    cacheStats?: Array<{
      model: string;
      inputTokens: number;
      inputTokensSinceCaching: number;
      cacheWriteTokens: number;
      cacheReadTokens: number;
      netSavings: number;
    }>;
  };
  // Email routing outcome breakdown for the "Email Routing" tab. Each
  // window is an array of { bucket, count } pairs sorted by count
  // desc; `bucket` is a server-formatted string like 'filtered',
  // 'subscription_match (user)', 'fallback_routed', 'uncategorized'.
  // Optional because old backends won't return it.
  emailRouting?: {
    month: Array<{ bucket: string; count: number }>;
    week: Array<{ bucket: string; count: number }>;
    day: Array<{ bucket: string; count: number }>;
  };
  // Cost-side companion to `emailRouting` — drives the comparison card
  // and the daily-cost line chart on the Email Routing tab. Both views
  // run server-side over `llm_costs.trigger = 'handleEmail'` so they
  // capture the full cost (intake classifier, lead decision, claim
  // scoring, semantic matcher, agent-mailbox triage, AND every
  // executeThread woken up by an email). Optional because old backends
  // won't return it.
  emailRoutingCost?: {
    todayVsAverage: {
      /** Today's running cost so far (USD). */
      today: number;
      /**
       * Average cost-by-this-time-of-day across the prior N days. The
       * "same elapsed time" anchor matters — comparing today's noon
       * spend to a full-day average would always look low until midnight.
       */
      priorAverage: number;
      /** Number of prior days that contributed to `priorAverage`. */
      priorDays: number;
    };
    /** 30-day daily-cost series (zero-filled). Today is the last entry. */
    daily: Array<{ date: string; cost: number }>;
  };
}

interface CohortRow {
  day: string;
  userId: string;
  userName: string | null;
  companyName: string | null;
  messages: number;
  whatsappMessages: number;
  tasks: number;
  calls: number;
  sessions: number;
}

interface UserStatsResponse {
  success: boolean;
  user: { id: string; name: string | null; companyName: string | null };
  days: string[];
  series: {
    messages: number[];
    tasks: number[];
    calls: number[];
    sessions: number[];
  };
}

type BreakdownAxis = 'outcome_kind' | 'quality_score';

type OutcomeKind =
  | 'delivered'
  | 'partial_delivery'
  | 'informed_unable'
  | 'failed'
  | 'cancelled'
  | 'unclassified'
  | 'waiting_on_user'
  | 'waiting_on_third_party'
  | 'in_flight';

type QualityScore =
  | 'good'
  | 'ineffective'
  | 'sloppy'
  | 'harmful'
  | 'unreviewed';

interface TaskOutcomesResponse {
  success: boolean;
  breakdownAxis?: BreakdownAxis;
  days: string[];
  granularity?: 'hour' | 'day';
  series: Record<string, number[]>;
  totals: Record<string, number | null>;
  durations: Array<{
    outcomeKind: string;
    p50Minutes: number;
    p90Minutes: number;
    count: number;
  }>;
}

// ─── Approach Outcomes types ──────────────────────────────────────────────────

interface ApproachOutcomesResponse {
  success: boolean;
  summary: {
    totalWithApproaches: number;
    totalTerminal: number;
  };
  effectiveness: Array<{
    approach: string;
    roles: Record<string, number>;
    total: number;
  }>;
  primaryDrivers: Array<{ approach: string; count: number }>;
  failureAttribution: Array<{ approach: string; count: number }>;
  recoveryPatterns: Array<{
    failedApproach: string;
    recoveredVia: string;
    count: number;
  }>;
}

const APPROACH_LABELS: Record<string, string> = {
  human_task_web: 'Human Task (Web)',
  human_task_call: 'Human Task (Call)',
  human_task_in_person: 'Human Task (In Person)',
  ai_call: 'AI Phone Call',
  browser: 'Browser Use',
  web_search: 'Web Search',
  email: 'Email',
  calendar: 'Calendar',
};

const TASK_CATEGORY_LABELS: Record<string, string> = {
  shopping_and_purchases: 'Shopping & purchases',
  restaurants_and_dining: 'Restaurants & dining',
  travel_and_transport: 'Travel & transport',
  events_and_entertainment: 'Events & entertainment',
  business_and_professional: 'Business & professional',
  home_and_property: 'Home & property',
  health_and_wellness: 'Health & wellness',
  personal_admin_and_finance: 'Personal admin & finance',
  people_and_relationships: 'People & relationships',
  education: 'Education',
  other: 'Other',
  uncategorised: 'Uncategorised',
};

interface TaskTypeRow {
  category: string;
  volume: number;
  delivered?: number;
  partialDelivery?: number;
  informedUnable?: number;
  failed?: number;
  cancelled?: number;
  good?: number;
  ineffective?: number;
  sloppy?: number;
  harmful?: number;
  approaches: Array<{
    approach: string;
    volume: number;
    delivered: number;
    failed: number;
  }>;
  subCategories?: Array<{
    slug: string;
    label: string;
    volume: number;
    delivered: number;
  }>;
}

interface ApproachRow {
  approach: string;
  volume: number;
  delivered?: number;
  partialDelivery?: number;
  informedUnable?: number;
  failed?: number;
  cancelled?: number;
  good?: number;
  ineffective?: number;
  sloppy?: number;
  harmful?: number;
  combinations: Array<{
    combination: string;
    volume: number;
    delivered: number;
  }>;
}

interface TaskDeepDiveResponse {
  breakdownAxis?: BreakdownAxis;
  taskTypes: TaskTypeRow[];
  approaches: ApproachRow[];
}

const ROLE_COLORS: Record<string, string> = {
  primary_driver: 'rgba(34, 197, 94, 0.8)',
  supporting: 'rgba(59, 130, 246, 0.7)',
  attempted_failed: 'rgba(251, 191, 36, 0.8)',
  blocked_task: 'rgba(239, 68, 68, 0.8)',
};

const ROLE_LABELS: Record<string, string> = {
  primary_driver: 'Primary Driver',
  supporting: 'Supporting',
  attempted_failed: 'Attempted (Failed)',
  blocked_task: 'Blocked Task',
};

// Outcome kinds that represent "terminal" states (complete / failed /
// cancelled_by_user). The breakdown table and delivery-rate denominator
// only consider these.
const TERMINAL_OUTCOMES: OutcomeKind[] = [
  'delivered',
  'partial_delivery',
  'informed_unable',
  'cancelled',
  'failed',
];

// Outcome kinds that represent non-terminal, still-alive tasks. These
// indicate possible issues (stuck awaiting user, abandoned in flight)
// and are surfaced as separate tiles + buckets.
const NON_TERMINAL_BUCKETS: OutcomeKind[] = [
  'waiting_on_user',
  'waiting_on_third_party',
  'in_flight',
];

// Response shape from `/platform-stats/task-outcomes/drill-down`. Themes
// are null when the backend skipped clustering (delivered / unclassified
// / non-terminal buckets), or when clustering was attempted but failed.
interface DrillDownTheme {
  name: string;
  description: string;
  threadIds: string[];
}

interface DrillDownRow {
  threadId: string;
  userName: string | null;
  startedAt: string;
  endedAt: string;
  themes: string[];
}

interface DrillDownResponse {
  success: boolean;
  breakdownAxis?: BreakdownAxis;
  outcomeKind: string;
  windowDays: number;
  total: number;
  rows: DrillDownRow[];
  themes: DrillDownTheme[] | null;
  themesSkipped: boolean;
  /** Opaque ID used to match the Pusher event that delivers themes asynchronously. */
  requestId?: string | null;
}

// Colour + label for each outcome kind. Ordered from best → worst so the
// stacked bar reads top-to-bottom in order of user satisfaction, ending
// with "still alive" buckets that are parked or in flight.
const OUTCOME_ORDER: OutcomeKind[] = [
  ...TERMINAL_OUTCOMES,
  'unclassified',
  ...NON_TERMINAL_BUCKETS,
];

const OUTCOME_META: Record<
  OutcomeKind,
  { label: string; color: string; description: string }
> = {
  delivered: {
    label: 'Delivered (Complete)',
    color: 'rgba(22, 163, 74, 0.85)',
    description: 'User got what they asked for, in full',
  },
  partial_delivery: {
    label: 'Delivered (Partial)',
    color: 'rgba(74, 222, 128, 0.85)',
    description: 'Some but not all of the request was delivered',
  },
  informed_unable: {
    label: 'Delivered (Unable)',
    color: 'rgba(134, 239, 172, 0.85)',
    description: 'Honestly reported back that it could not be done',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'rgba(234, 179, 8, 0.85)',
    description: 'User cancelled before completion',
  },
  failed: {
    label: 'Failed',
    color: 'rgba(239, 68, 68, 0.85)',
    description: 'Technical failure or agent gave up mid-task',
  },
  unclassified: {
    label: 'Unclassified',
    color: 'rgba(209, 213, 219, 0.55)',
    description:
      'Terminal task that never received an outcome (agent did not self-report; may indicate a crash)',
  },
  waiting_on_user: {
    label: 'Waiting on user',
    color: 'rgba(59, 130, 246, 0.75)',
    description:
      'Parked awaiting a user response — long-lived rows here may indicate the ask went unnoticed',
  },
  waiting_on_third_party: {
    label: 'Waiting on 3rd party',
    color: 'rgba(168, 85, 247, 0.75)',
    description:
      'Waiting on an external party (e.g. a human task on the Dream team)',
  },
  in_flight: {
    label: 'In flight',
    color: 'rgba(14, 165, 233, 0.75)',
    description:
      'Still processing — if this persists, the task is effectively abandoned and may need intervention',
  },
};

// ─── Quality Score display metadata ──────────────────────────────────────────

const QUALITY_SCORE_ORDER: QualityScore[] = [
  'good',
  'ineffective',
  'sloppy',
  'harmful',
  'unreviewed',
];

const QUALITY_SCORE_META: Record<
  QualityScore,
  { label: string; color: string; description: string }
> = {
  good: {
    label: 'Good',
    color: 'rgba(34, 197, 94, 0.85)',
    description: 'Task was completed well with no quality issues',
  },
  ineffective: {
    label: 'Ineffective',
    color: 'rgba(250, 204, 21, 0.85)',
    description: 'Task result did not achieve what was needed',
  },
  sloppy: {
    label: 'Sloppy',
    color: 'rgba(249, 115, 22, 0.85)',
    description: 'Task was done carelessly with avoidable mistakes',
  },
  harmful: {
    label: 'Harmful',
    color: 'rgba(239, 68, 68, 0.85)',
    description: 'Task caused damage or negative consequences',
  },
  unreviewed: {
    label: 'Unreviewed',
    color: 'rgba(209, 213, 219, 0.55)',
    description: 'Task has not been quality-reviewed yet',
  },
};

interface AiCallsStatsResponse {
  success: boolean;
  days: string[];
  categoryGroupSeries: Record<string, number[]>;
  assessmentCategories: Array<{ category: string; count: number }>;
  responseLatencies: number[];
  totals: {
    totalCalls: number;
    connectedCalls: number;
    assessedCalls: number;
    connectionRate: number | null;
    successRate: number | null;
    successCount: number;
  };
}

const DAY_OPTIONS = [3, 7, 14, 30, 60, 90];
const TABS_WITHOUT_RANGE = new Set<Tab>([
  'scaling',
  'growth',
  'browser-ladder',
  'web-work-mix',
  'ops-capacity',
  'overview',
  'onboarding',
  'acquisition',
  'launches',
  'promotions',
  'promotions',
  'ops-workers',
  'campaign-codes',
  'feature-flags',
  'user-experience',
  'payments',
]);
// ─── Outcome date range ─────────────────────────────────────────────────────

/**
 * Discriminated union representing the active window for the Outcomes
 * tab. Preset is the standard rolling-window mode (last N days). Custom
 * lets the user compare specific calendar windows ("last week" vs "this
 * week", etc.) — the backend resolves either form via `resolveDateRange`.
 */
type DateRange =
  | { mode: 'yesterday' }
  | { mode: 'preset'; days: number }
  | { mode: 'custom'; start: string; end: string };

const formatDateInput = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const today = () => formatDateInput(new Date());
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatDateInput(d);
};

/** Build the query string fragment for the outcome endpoints. */
const rangeToQuery = (r: DateRange): string => {
  if (r.mode === 'yesterday') {
    const y = daysAgo(1);
    return `startDate=${encodeURIComponent(y)}&endDate=${encodeURIComponent(y)}`;
  }
  if (r.mode === 'preset') return `days=${r.days}`;
  return `startDate=${encodeURIComponent(r.start)}&endDate=${encodeURIComponent(r.end)}`;
};

/** Human-readable label for tooltips and the drill-down header. */
const rangeLabel = (r: DateRange): string => {
  if (r.mode === 'yesterday') return 'yesterday';
  if (r.mode === 'preset')
    return `last ${r.days} day${r.days === 1 ? '' : 's'}`;
  return `${r.start} → ${r.end}`;
};

/** Append filterUserId param if a user is selected. */
const userFilterParam = (uid: string | null): string =>
  uid ? `&filterUserId=${encodeURIComponent(uid)}` : '';

/** Build the taskCategories query param from the toggle state. */
const taskCategoriesParam = (cats: {
  user_triggered: boolean;
  routine: boolean;
  internal: boolean;
}): string => {
  const active = Object.entries(cats)
    .filter(([, v]) => v)
    .map(([k]) => k);
  if (active.length === 0) return '';
  return `&taskCategories=${active.join(',')}`;
};

/** Build the breakdownAxis query param. */
const breakdownAxisParam = (axis: BreakdownAxis): string =>
  axis === 'quality_score' ? '&breakdownAxis=quality_score' : '';

/** Build the taskType query param from the selected domain category. */
const taskTypeParam = (taskType: string | null): string =>
  taskType ? `&taskType=${encodeURIComponent(taskType)}` : '';

/** Domain-level task category options for the filter dropdown. */
const TASK_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'restaurants_and_dining', label: 'Restaurants & Dining' },
  { value: 'travel_and_transport', label: 'Travel & Transport' },
  { value: 'shopping_and_purchases', label: 'Shopping & Purchases' },
  { value: 'events_and_entertainment', label: 'Events & Entertainment' },
  { value: 'business_and_professional', label: 'Business & Professional' },
  { value: 'home_and_property', label: 'Home & Property' },
  { value: 'health_and_wellness', label: 'Health & Wellness' },
  { value: 'personal_admin_and_finance', label: 'Personal Admin & Finance' },
  { value: 'people_and_relationships', label: 'People & Relationships' },
  { value: 'education', label: 'Education' },
  { value: 'other', label: 'Other' },
];

// ─── Chart helpers ───────────────────────────────────────────────────────────

const barChartOptions = (title: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: title,
      font: {
        family: 'Instrument Sans, sans-serif',
        size: 14,
        weight: 600 as const,
      },
      color: '#222',
      padding: { bottom: 12 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { family: 'Instrument Sans, sans-serif', size: 11 },
        color: '#888',
        maxRotation: 45,
        autoSkip: true,
        maxTicksLimit: 15,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f0f0f0' },
      ticks: {
        font: { family: 'Instrument Sans, sans-serif', size: 11 },
        color: '#888',
        precision: 0,
      },
    },
  },
});

const lineChartOptions = (title: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'top' as const },
    title: {
      display: true,
      text: title,
      font: {
        family: 'Instrument Sans, sans-serif',
        size: 14,
        weight: 600 as const,
      },
      color: '#222',
      padding: { bottom: 12 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { family: 'Instrument Sans, sans-serif', size: 11 },
        color: '#888',
        maxRotation: 45,
        autoSkip: true,
        maxTicksLimit: 15,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f0f0f0' },
      ticks: {
        font: { family: 'Instrument Sans, sans-serif', size: 11 },
        color: '#888',
        precision: 0,
      },
    },
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const PlatformStats: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StatsResponse | null>(null);
  // Scaling is the default view: it's the tab Ben opens the dashboard for.
  // The page lives in the path so any page can be shared as a link; the
  // query string carries per-page state (e.g. Ops Capacity's ?from=&to=).
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const legacyTabParam = searchParams.get('tab');
  const tab = resolveTab(location.pathname, legacyTabParam);
  const activeGroup = groupOf(tab);
  const canonicalPath = platformStatsPath(tab);
  useEffect(() => {
    if (location.pathname === canonicalPath && legacyTabParam == null) return;
    const params = new URLSearchParams(location.search);
    params.delete('tab');
    const query = params.toString();
    navigate(`${canonicalPath}${query ? `?${query}` : ''}`, { replace: true });
  }, [
    canonicalPath,
    legacyTabParam,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Single shared date range state used by ALL tabs (Charts, User
  // Activity, Outcomes). The active window is either a rolling preset
  // (last N days) or an explicit calendar range — backend
  // `resolveDateRange` accepts both forms. `customStart` / `customEnd`
  // back the two date inputs and only get committed to `range` when
  // both are non-empty and ordered, so partial typing doesn't fire
  // half-baked fetches.
  const [range, setRange] = useState<DateRange>({
    mode: 'preset',
    days: 30,
  });
  const [customStart, setCustomStart] = useState<string>(daysAgo(7));
  const [customEnd, setCustomEnd] = useState<string>(today());
  const [perUser, setPerUser] = useState(false);
  const [retentionView, setRetentionView] = useState<'daily' | 'weekly'>(
    'daily',
  );

  // Cohort state
  const [cohortRows, setCohortRows] = useState<CohortRow[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);

  // User drill-down state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  // Outcomes state
  const [outcomes, setOutcomes] = useState<TaskOutcomesResponse | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(false);

  // Task category filter — which sub-thread categories to include in
  // outcome graphs. Default: only user-triggered tasks enabled.
  const [taskCategories, setTaskCategories] = useState<{
    user_triggered: boolean;
    routine: boolean;
    internal: boolean;
  }>({ user_triggered: true, routine: false, internal: false });

  // Task type (domain category) filter — optional single category
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);

  // Breakdown axis toggle — outcome_kind (default) or quality_score
  const [breakdownAxis, setBreakdownAxis] =
    useState<BreakdownAxis>('outcome_kind');

  // Approach outcomes state
  const [approachData, setApproachData] =
    useState<ApproachOutcomesResponse | null>(null);
  const [approachLoading, setApproachLoading] = useState(false);

  // Task deepdive state
  const [deepDiveData, setDeepDiveData] = useState<TaskDeepDiveResponse | null>(
    null,
  );
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);

  // AI Phone Calls state
  const [aiCallsData, setAiCallsData] = useState<AiCallsStatsResponse | null>(
    null,
  );
  const [aiCallsLoading, setAiCallsLoading] = useState(false);

  // Browser & MFA state
  const [browserMfaData, setBrowserMfaData] =
    useState<BrowserSessionsResponse | null>(null);
  const [browserMfaLoading, setBrowserMfaLoading] = useState(false);

  // User filter state
  const [userList, setUserList] = useState<
    Array<{ id: string; name: string | null; companyName: string | null }>
  >([]);
  const [filterUserId, setFilterUserId] = useState<string | null>(null);
  // Daily Cost chart selection lives here so it survives the Costs tab
  // re-mounting while the page-level stats reload.
  const [dailyCostBreakdown, setDailyCostBreakdown] =
    useState<DailyCostDimension>('model');
  const [dailyCostFilters, setDailyCostFilters] = useState<DailyCostFilters>(
    {},
  );
  // Model Breakdown table has its own trigger / agent selection so the
  // per-model cache hit rate can be read for one workload at a time.
  const [modelBreakdownFilters, setModelBreakdownFilters] =
    useState<DailyCostFilters>({});

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          users: Array<{
            id: string;
            name: string | null;
            companyName: string | null;
          }>;
        }>('/platform-stats/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) setUserList(res.data.users);
      } catch {
        // Non-critical — dropdown just stays empty
      }
    })();
  }, [getAccessTokenSilently]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<StatsResponse>(
        `/platform-stats?${rangeToQuery(range)}${userFilterParam(filterUserId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setData(res.data);
        setHasAccess(true);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasAccess(false);
      } else {
        setError('Failed to load stats');
      }
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, range, filterUserId]);

  const fetchCohorts = useCallback(async () => {
    setCohortLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{ success: boolean; rows: CohortRow[] }>(
        `/platform-stats/cohorts?${rangeToQuery(range)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setCohortRows(res.data.rows);
    } catch {
      setError('Failed to load cohort data');
    } finally {
      setCohortLoading(false);
    }
  }, [getAccessTokenSilently, range]);

  const fetchUserStats = useCallback(
    async (uid: string) => {
      setUserLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<UserStatsResponse>(
          `/platform-stats/user/${encodeURIComponent(uid)}?${rangeToQuery(range)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setUserStats(res.data);
      } catch {
        setError('Failed to load user stats');
      } finally {
        setUserLoading(false);
      }
    },
    [getAccessTokenSilently, range],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (tab === 'cohorts') fetchCohorts();
  }, [tab, fetchCohorts]);

  const fetchOutcomes = useCallback(async () => {
    setOutcomesLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<TaskOutcomesResponse>(
        `/platform-stats/task-outcomes?${rangeToQuery(range)}${userFilterParam(filterUserId)}${taskCategoriesParam(taskCategories)}${taskTypeParam(selectedTaskType)}${breakdownAxisParam(breakdownAxis)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setOutcomes(res.data);
    } catch {
      setError('Failed to load task outcome data');
    } finally {
      setOutcomesLoading(false);
    }
  }, [
    getAccessTokenSilently,
    range,
    filterUserId,
    taskCategories,
    selectedTaskType,
    breakdownAxis,
  ]);

  const fetchApproachOutcomes = useCallback(async () => {
    setApproachLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<ApproachOutcomesResponse>(
        `/platform-stats/approach-outcomes?${rangeToQuery(range)}${userFilterParam(filterUserId)}${taskCategoriesParam(taskCategories)}${taskTypeParam(selectedTaskType)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setApproachData(res.data);
    } catch {
      // Non-critical — don't override top-level error
    } finally {
      setApproachLoading(false);
    }
  }, [
    getAccessTokenSilently,
    range,
    filterUserId,
    taskCategories,
    selectedTaskType,
  ]);

  const fetchDeepDive = useCallback(async () => {
    setDeepDiveLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<TaskDeepDiveResponse>(
        `/platform-stats/task-deepdive?${rangeToQuery(range)}${userFilterParam(filterUserId)}${taskCategoriesParam(taskCategories)}${taskTypeParam(selectedTaskType)}${breakdownAxisParam(breakdownAxis)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDeepDiveData(res.data);
    } catch {
      // Non-critical
    } finally {
      setDeepDiveLoading(false);
    }
  }, [
    getAccessTokenSilently,
    range,
    filterUserId,
    taskCategories,
    selectedTaskType,
    breakdownAxis,
  ]);

  useEffect(() => {
    if (tab === 'outcomes') {
      fetchOutcomes();
      fetchApproachOutcomes();
      fetchDeepDive();
    }
  }, [tab, fetchOutcomes, fetchApproachOutcomes, fetchDeepDive]);

  const fetchAiCalls = useCallback(async () => {
    setAiCallsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<AiCallsStatsResponse>(
        `/platform-stats/ai-calls?${rangeToQuery(range)}${userFilterParam(filterUserId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setAiCallsData(res.data);
    } catch {
      setError('Failed to load AI calls data');
    } finally {
      setAiCallsLoading(false);
    }
  }, [getAccessTokenSilently, range, filterUserId]);

  useEffect(() => {
    if (tab === 'ai-calls') fetchAiCalls();
  }, [tab, fetchAiCalls]);

  const fetchBrowserMfa = useCallback(async () => {
    setBrowserMfaLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<BrowserSessionsResponse>(
        `/platform-stats/browser-sessions?${rangeToQuery(range)}${userFilterParam(filterUserId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setBrowserMfaData(res.data);
    } catch {
      setError('Failed to load browser & MFA data');
    } finally {
      setBrowserMfaLoading(false);
    }
  }, [getAccessTokenSilently, range, filterUserId]);

  useEffect(() => {
    if (tab === 'browser-mfa') fetchBrowserMfa();
  }, [tab, fetchBrowserMfa]);

  useEffect(() => {
    if (selectedUserId) fetchUserStats(selectedUserId);
  }, [selectedUserId, fetchUserStats]);

  // ─── Cohort helpers ────────────────────────────────────────────────────────
  // NOTE: keep this (and any other hooks) ABOVE the access-denied early
  // return. React's rules-of-hooks require the same hook order on every
  // render, so a conditional early return above a hook will blow up with
  // "Rendered fewer hooks than expected" the moment hasAccess flips.
  const cohortByDay = useMemo(() => {
    const map = new Map<string, CohortRow[]>();
    for (const r of cohortRows) {
      const key = r.day;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [cohortRows]);

  if (hasAccess === false) {
    return (
      <div className='platform-stats'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  const labels = data?.days.map(formatLabel) ?? [];

  const makeBarData = (values: number[], color: string) => ({
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: color,
        borderRadius: 3,
        barPercentage: 0.7,
      },
    ],
  });

  const dauMauData = {
    labels,
    datasets: [
      {
        label: 'DAU',
        data: data?.series.dau ?? [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
      {
        label: 'MAU (28-day)',
        data: data?.series.mau ?? [],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.08)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };

  const handleUserClick = (uid: string) => {
    setSelectedUserId(uid);
    setUserStats(null);
  };

  const userBarLabels = userStats?.days.map(formatLabel) ?? [];
  const makeUserBar = (values: number[], color: string) => ({
    labels: userBarLabels,
    datasets: [
      {
        data: values,
        backgroundColor: color,
        borderRadius: 3,
        barPercentage: 0.7,
      },
    ],
  });

  return (
    <div className='platform-stats'>
      <MobileNavToggle />
      <div className='header'>
        <h2>Platform Stats</h2>
        <nav
          className='tab-selector stats-categories'
          aria-label='Platform Stats categories'
        >
          {TAB_GROUPS.map((g) => (
            <Link
              key={g.key}
              to={platformStatsPath(g.tabs[0].key)}
              className={`tab-btn ${g === activeGroup ? 'active' : ''}`}
              aria-current={g === activeGroup ? 'true' : undefined}
            >
              {g.label}
            </Link>
          ))}
        </nav>

        {/* Single shared date-range selector for the legacy tabs and
            Costs. Overview and Onboarding manage their own timeframes,
            so the shared selector is hidden there. */}
        {!TABS_WITHOUT_RANGE.has(tab) && (
          <div className='day-selector outcome-range-selector'>
            <button
              className={`day-btn ${range.mode === 'yesterday' ? 'active' : ''}`}
              onClick={() => setRange({ mode: 'yesterday' })}
            >
              Yesterday
            </button>
            {DAY_OPTIONS.map((d) => {
              const active = range.mode === 'preset' && range.days === d;
              return (
                <button
                  key={d}
                  className={`day-btn ${active ? 'active' : ''}`}
                  onClick={() => setRange({ mode: 'preset', days: d })}
                >
                  {d}d
                </button>
              );
            })}
            <button
              className={`day-btn ${range.mode === 'custom' ? 'active' : ''}`}
              onClick={() => {
                // Switch into custom mode using the currently-staged
                // start/end dates. If the staged values are blank or
                // invalid we fall back to a sensible "last 7 days"
                // window so the user always sees data immediately.
                const start =
                  customStart && customEnd && customStart <= customEnd
                    ? customStart
                    : daysAgo(7);
                const end =
                  customStart && customEnd && customStart <= customEnd
                    ? customEnd
                    : today();
                setCustomStart(start);
                setCustomEnd(end);
                setRange({ mode: 'custom', start, end });
              }}
              title='Pick a custom calendar range'
            >
              Custom
            </button>

            {range.mode === 'custom' && (
              <div className='custom-range-inputs'>
                <input
                  type='date'
                  className='date-input'
                  value={customStart}
                  max={customEnd || today()}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomStart(v);
                    if (v && customEnd && v <= customEnd) {
                      setRange({
                        mode: 'custom',
                        start: v,
                        end: customEnd,
                      });
                    }
                  }}
                  aria-label='Start date'
                />
                <span className='range-arrow'>→</span>
                <input
                  type='date'
                  className='date-input'
                  value={customEnd}
                  min={customStart || undefined}
                  max={today()}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomEnd(v);
                    if (v && customStart && customStart <= v) {
                      setRange({
                        mode: 'custom',
                        start: customStart,
                        end: v,
                      });
                    }
                  }}
                  aria-label='End date'
                />
              </div>
            )}

            {userList.length > 0 &&
              tab !== 'charts' &&
              tab !== 'cohorts' &&
              tab !== 'user-lookup' && (
                <select
                  className='user-filter-select'
                  value={filterUserId || ''}
                  onChange={(e) => setFilterUserId(e.target.value || null)}
                >
                  <option value=''>All users</option>
                  {userList.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.id}
                      {u.companyName ? ` (${u.companyName})` : ''}
                    </option>
                  ))}
                </select>
              )}
          </div>
        )}
      </div>
      <nav className='stats-subnav' aria-label={`${activeGroup.label} pages`}>
        {activeGroup.tabs.map((t) => (
          <Link
            key={t.key}
            to={platformStatsPath(t.key)}
            className={`stats-subnav-tab ${t.key === tab ? 'active' : ''}`}
            aria-current={t.key === tab ? 'page' : undefined}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      {error && <div className='msg error'>{error}</div>}

      {tab === 'scaling' && <ScalingDashboard />}
      {tab === 'growth' && <GrowthDashboard />}

      {tab === 'browser-ladder' && <BrowserLadderDashboard />}
      {tab === 'web-work-mix' && <WebWorkMixDashboard />}

      {tab === 'ops-capacity' && <OpsCapacityDashboard />}

      {tab === 'overview' && <OverviewDashboard />}

      {tab === 'onboarding' && <ActivationDashboard />}

      {tab === 'acquisition' && <AcquisitionDashboard />}

      {tab === 'users' && <UserTypesPanel />}

      {tab === 'launches' && <LaunchesDashboard />}

      {tab === 'promotions' && <PromotionsDashboard />}

      {tab === 'charts' && (
        <>
          {loading ? (
            <div className='loading'>Loading...</div>
          ) : data ? (
            <div className='charts-grid'>
              <div className='chart-card wide'>
                <div className='chart-header-row'>
                  <span className='stat-badge'>
                    Total Onboarded: <strong>{data.totalOnboardedUsers}</strong>
                  </span>
                </div>
                <Line
                  data={dauMauData}
                  options={lineChartOptions('DAU / MAU (onboarded users only)')}
                />
              </div>
              <div className='per-user-toggle'>
                <button
                  className={`toggle-btn ${perUser ? 'active' : ''}`}
                  onClick={() => setPerUser((v) => !v)}
                >
                  {perUser ? '# per Onboarded User' : 'Totals'}
                </button>
              </div>
              <div className='chart-card'>
                <Bar
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'In-App',
                        data: perUser
                          ? data.series.messagesInApp.map((v, i) =>
                              data.series.onboardedUsers[i] > 0
                                ? Math.round(
                                    (v / data.series.onboardedUsers[i]) * 100,
                                  ) / 100
                                : 0,
                            )
                          : data.series.messagesInApp,
                        backgroundColor: 'rgba(99, 102, 241, 0.7)',
                        borderRadius: 3,
                        barPercentage: 0.7,
                      },
                      {
                        label: 'WhatsApp',
                        data: perUser
                          ? data.series.messagesWhatsapp.map((v, i) =>
                              data.series.onboardedUsers[i] > 0
                                ? Math.round(
                                    (v / data.series.onboardedUsers[i]) * 100,
                                  ) / 100
                                : 0,
                            )
                          : data.series.messagesWhatsapp,
                        backgroundColor: 'rgba(37, 211, 102, 0.7)',
                        borderRadius: 3,
                        barPercentage: 0.7,
                      },
                    ],
                  }}
                  options={{
                    ...barChartOptions(
                      perUser ? 'Messages per Onboarded User' : 'Messages Sent',
                    ),
                    plugins: {
                      ...barChartOptions('Messages Sent').plugins,
                      legend: { display: true, position: 'top' as const },
                    },
                    scales: {
                      ...barChartOptions('Messages Sent').scales,
                      x: {
                        ...barChartOptions('Messages Sent').scales.x,
                        stacked: true,
                      },
                      y: {
                        ...barChartOptions('Messages Sent').scales.y,
                        stacked: true,
                      },
                    },
                  }}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    perUser
                      ? data.series.tasks.map((v, i) =>
                          data.series.onboardedUsers[i] > 0
                            ? Math.round(
                                (v / data.series.onboardedUsers[i]) * 100,
                              ) / 100
                            : 0,
                        )
                      : data.series.tasks,
                    'rgba(244, 114, 182, 0.7)',
                  )}
                  options={barChartOptions(
                    perUser ? 'Tasks per Onboarded User' : 'Tasks Created',
                  )}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    data.series.standups.map((v, i) =>
                      data.series.standupEnabledUsers[i] > 0
                        ? Math.round(
                            (v / data.series.standupEnabledUsers[i]) * 10000,
                          ) / 100
                        : 0,
                    ),
                    'rgba(251, 191, 36, 0.7)',
                  )}
                  options={{
                    ...barChartOptions('Standup Completion %'),
                    scales: {
                      ...barChartOptions('Standup Completion %').scales,
                      y: {
                        ...barChartOptions('Standup Completion %').scales.y,
                        max: 100,
                        ticks: {
                          ...barChartOptions('Standup Completion %').scales.y
                            .ticks,
                          callback: (v: any) => `${v}%`,
                        },
                      },
                    },
                    plugins: {
                      ...barChartOptions('Standup Completion %').plugins,
                      tooltip: {
                        callbacks: {
                          label: (ctx: any) => `${ctx.raw.toFixed(1)}%`,
                        },
                      },
                    },
                  }}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    perUser
                      ? data.series.calls.map((v, i) =>
                          data.series.onboardedUsers[i] > 0
                            ? Math.round(
                                (v / data.series.onboardedUsers[i]) * 100,
                              ) / 100
                            : 0,
                        )
                      : data.series.calls,
                    'rgba(34, 197, 94, 0.7)',
                  )}
                  options={barChartOptions(
                    perUser ? 'Phone Calls per Onboarded User' : 'Phone Calls',
                  )}
                />
              </div>
              {data.series.sessions && (
                <div className='chart-card'>
                  <Bar
                    data={makeBarData(
                      perUser
                        ? data.series.sessions.map((v, i) =>
                            (data.series.sessionsActiveUsers?.[i] ?? 0) > 0
                              ? Math.round(
                                  (v / data.series.sessionsActiveUsers[i]) *
                                    100,
                                ) / 100
                              : 0,
                          )
                        : data.series.sessions,
                      'rgba(14, 165, 233, 0.7)',
                    )}
                    options={barChartOptions(
                      perUser
                        ? 'Sessions per Active User'
                        : 'Sessions (30-min gap)',
                    )}
                  />
                </div>
              )}
              {data.llmCosts && (
                <>
                  <div className='chart-card'>
                    <Line
                      data={{
                        labels,
                        datasets: [
                          {
                            label: 'Total Spend',
                            data: data.llmCosts.totalCost,
                            borderColor: 'rgb(239, 68, 68)',
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                          },
                        ],
                      }}
                      options={{
                        ...lineChartOptions('Daily LLM Expenditure ($)'),
                        plugins: {
                          ...lineChartOptions('Daily LLM Expenditure ($)')
                            .plugins,
                          tooltip: {
                            callbacks: {
                              label: (ctx: any) =>
                                `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                            },
                          },
                        },
                        scales: {
                          ...lineChartOptions('Daily LLM Expenditure ($)')
                            .scales,
                          y: {
                            ...lineChartOptions('Daily LLM Expenditure ($)')
                              .scales.y,
                            ticks: {
                              ...lineChartOptions('Daily LLM Expenditure ($)')
                                .scales.y.ticks,
                              callback: (v: any) => formatUSD(v),
                            },
                          },
                        },
                      }}
                    />
                  </div>
                  <div className='chart-card'>
                    <Line
                      data={{
                        labels,
                        datasets: [
                          {
                            label: 'Avg per Account',
                            data: data.llmCosts.avgCostPerUser,
                            borderColor: 'rgb(168, 85, 247)',
                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                          },
                        ],
                      }}
                      options={{
                        ...lineChartOptions('Avg Cost per Account per Day ($)'),
                        plugins: {
                          ...lineChartOptions(
                            'Avg Cost per Account per Day ($)',
                          ).plugins,
                          tooltip: {
                            callbacks: {
                              label: (ctx: any) =>
                                `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                            },
                          },
                        },
                        scales: {
                          ...lineChartOptions(
                            'Avg Cost per Account per Day ($)',
                          ).scales,
                          y: {
                            ...lineChartOptions(
                              'Avg Cost per Account per Day ($)',
                            ).scales.y,
                            ticks: {
                              ...lineChartOptions(
                                'Avg Cost per Account per Day ($)',
                              ).scales.y.ticks,
                              callback: (v: any) => formatUSD(v),
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : null}

          {data?.returnCohort && data.returnCohort.cohortSize > 0 && (
            <div className='return-cohorts-section'>
              <div className='cohort-title-row'>
                <div>
                  <h3>Return Cohort</h3>
                  <p className='cohort-subtitle'>
                    {retentionView === 'daily'
                      ? `% of eligible users active on day N after their signup (${data.returnCohort.cohortSize} total onboarded)`
                      : 'Retention % by onboarding week (messages, tasks, or calls)'}
                  </p>
                </div>
                <div className='retention-toggle'>
                  <button
                    className={`toggle-btn ${retentionView === 'daily' ? 'active' : ''}`}
                    onClick={() => setRetentionView('daily')}
                  >
                    Daily
                  </button>
                  <button
                    className={`toggle-btn ${retentionView === 'weekly' ? 'active' : ''}`}
                    onClick={() => setRetentionView('weekly')}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              {retentionView === 'daily' ? (
                <div className='cohort-heatmap-wrap'>
                  <table className='cohort-heatmap'>
                    <thead>
                      <tr>
                        {data.returnCohort.daily.map((_: number, i: number) => (
                          <th key={i}>D{i}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {data.returnCohort.daily.map(
                          (active: number, i: number) => {
                            const eligible =
                              data.returnCohort.dailyCohortSizes?.[i] ??
                              data.returnCohort.cohortSize;
                            const pct =
                              eligible > 0
                                ? Math.round((active / eligible) * 100)
                                : 0;
                            const opacity = Math.min(pct / 100, 1);
                            return (
                              <td
                                key={i}
                                className='cohort-cell'
                                style={{
                                  backgroundColor: `rgba(59, 130, 246, ${opacity * 0.8 + 0.05})`,
                                  color: opacity > 0.45 ? '#fff' : '#333',
                                }}
                                title={`${active}/${eligible} eligible (${pct}%)`}
                              >
                                {pct > 0 ? `${pct}%` : '–'}
                              </td>
                            );
                          },
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='cohort-heatmap-wrap'>
                  <table className='cohort-heatmap'>
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th>Size</th>
                        {Array.from({ length: 13 }, (_, i) => (
                          <th key={i}>W{i}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.returnCohort.weeklyCohorts.map((c) => (
                        <tr key={c.cohortWeek}>
                          <td className='cohort-week-label'>{c.cohortWeek}</td>
                          <td className='cohort-size'>{c.cohortSize}</td>
                          {c.retention.map((active: number, wi: number) => {
                            const pct =
                              c.cohortSize > 0
                                ? Math.round((active / c.cohortSize) * 100)
                                : 0;
                            const opacity = Math.min(pct / 100, 1);
                            return (
                              <td
                                key={wi}
                                className='cohort-cell'
                                style={{
                                  backgroundColor: `rgba(59, 130, 246, ${opacity * 0.8 + 0.05})`,
                                  color: opacity > 0.45 ? '#fff' : '#333',
                                }}
                                title={`${active}/${c.cohortSize} (${pct}%)`}
                              >
                                {pct > 0 ? `${pct}%` : '–'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'cohorts' && (
        <div className='cohort-section'>
          {cohortLoading ? (
            <div className='loading'>Loading cohort data...</div>
          ) : (
            <div className='cohort-layout'>
              <div className='cohort-table-wrap'>
                {cohortByDay.map(([day, users]: [string, CohortRow[]]) => (
                  <div key={day} className='cohort-day-group'>
                    <div className='cohort-day-header'>
                      <span className='cohort-day-label'>
                        {formatLabel(day)}
                      </span>
                      <span className='cohort-day-count'>
                        {users.length} user{users.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <table className='cohort-table'>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Company</th>
                          <th>Messages</th>
                          <th>% WA</th>
                          <th>Tasks</th>
                          <th>Calls</th>
                          <th>Sessions</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u: CohortRow) => (
                          <tr
                            key={u.userId}
                            className={`cohort-row ${selectedUserId === u.userId ? 'selected' : ''}`}
                            onClick={() => handleUserClick(u.userId)}
                          >
                            <td className='user-name'>
                              {u.userName || u.userId?.slice(0, 12) || '–'}
                            </td>
                            <td className='company-name'>
                              {u.companyName || '–'}
                            </td>
                            <td>{u.messages}</td>
                            <td>
                              {u.messages > 0
                                ? `${Math.round((u.whatsappMessages / u.messages) * 100)}%`
                                : '–'}
                            </td>
                            <td>{u.tasks}</td>
                            <td>{u.calls}</td>
                            <td>{u.sessions}</td>
                            <td className='total'>
                              {u.messages + u.tasks + u.calls}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {selectedUserId && (
                <div className='user-detail-panel'>
                  <div className='user-detail-header'>
                    <h3>
                      {userStats?.user.name ||
                        selectedUserId?.slice(0, 16) ||
                        '–'}
                    </h3>
                    {userStats?.user.companyName && (
                      <span className='user-detail-company'>
                        {userStats.user.companyName}
                      </span>
                    )}
                    <button
                      className='close-btn'
                      onClick={() => {
                        setSelectedUserId(null);
                        setUserStats(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {userLoading ? (
                    <div className='loading'>Loading...</div>
                  ) : userStats ? (
                    <div className='user-charts'>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.messages,
                            'rgba(99, 102, 241, 0.7)',
                          )}
                          options={barChartOptions('Messages')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.tasks,
                            'rgba(244, 114, 182, 0.7)',
                          )}
                          options={barChartOptions('Tasks')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.calls,
                            'rgba(34, 197, 94, 0.7)',
                          )}
                          options={barChartOptions('Calls')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.sessions,
                            'rgba(14, 165, 233, 0.7)',
                          )}
                          options={barChartOptions('Sessions')}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'outcomes' && (
        <div className='outcomes-section'>
          {/* ── Filter bar ──────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            {/* Task category toggles (left) */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(
                [
                  ['user_triggered', 'User-Triggered'],
                  ['routine', 'Routine'],
                  ['internal', 'Internal'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() =>
                    setTaskCategories((prev) => {
                      const activeCount =
                        Object.values(prev).filter(Boolean).length;
                      if (prev[key] && activeCount <= 1) return prev;
                      return { ...prev, [key]: !prev[key] };
                    })
                  }
                  style={{
                    padding: '4px 12px',
                    borderRadius: 6,
                    border: '1px solid',
                    borderColor: taskCategories[key] ? '#3b82f6' : '#d1d5db',
                    background: taskCategories[key] ? '#dbeafe' : '#f9fafb',
                    color: taskCategories[key] ? '#1d4ed8' : '#6b7280',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Task type (domain category) dropdown */}
            <select
              value={selectedTaskType || ''}
              onChange={(e) => setSelectedTaskType(e.target.value || null)}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: selectedTaskType ? '#dbeafe' : '#f9fafb',
                color: selectedTaskType ? '#1d4ed8' : '#6b7280',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <option value=''>All Task Types</option>
              {TASK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Breakdown axis toggle (right) */}
            <div
              style={{
                display: 'flex',
                gap: 0,
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid #d1d5db',
              }}
            >
              {(
                [
                  ['outcome_kind', 'Task Outcome'],
                  ['quality_score', 'Quality Score'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setBreakdownAxis(value)}
                  style={{
                    padding: '4px 12px',
                    border: 'none',
                    background: breakdownAxis === value ? '#3b82f6' : '#f9fafb',
                    color: breakdownAxis === value ? '#fff' : '#6b7280',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {outcomesLoading ? (
            <div className='loading'>Loading outcome data...</div>
          ) : outcomes ? (
            <OutcomesView
              outcomes={outcomes}
              dateRange={range}
              filterUserId={filterUserId}
              taskCategories={taskCategories}
              selectedTaskType={selectedTaskType}
              breakdownAxis={breakdownAxis}
              deepDive={deepDiveData}
              deepDiveLoading={deepDiveLoading}
            />
          ) : (
            <div className='loading'>No data.</div>
          )}

          {/* Approach Outcomes — which mechanisms drove results */}
          {approachLoading ? (
            <div className='loading' style={{ marginTop: 32 }}>
              Loading approach data...
            </div>
          ) : approachData && approachData.summary.totalWithApproaches > 0 ? (
            <ApproachOutcomesView data={approachData} />
          ) : null}
        </div>
      )}

      {tab === 'email-routing' && (
        <div className='costs-section'>
          {loading ? (
            <div className='loading'>Loading email routing data...</div>
          ) : data?.emailRouting ? (
            <EmailRoutingView
              data={data.emailRouting}
              cost={data.emailRoutingCost}
            />
          ) : (
            <div className='loading'>No email routing data available.</div>
          )}
        </div>
      )}

      {tab === 'costs' && (
        <div className='costs-section'>
          {loading ? (
            <div className='loading'>Loading cost data...</div>
          ) : data?.llmCosts ? (
            <CostsView
              data={data}
              dailyCost={{
                baseQuery: `${rangeToQuery(range)}${userFilterParam(filterUserId)}`,
                breakdown: dailyCostBreakdown,
                filters: dailyCostFilters,
                onBreakdownChange: setDailyCostBreakdown,
                onFiltersChange: setDailyCostFilters,
              }}
              modelBreakdown={{
                baseQuery: `${rangeToQuery(range)}${userFilterParam(filterUserId)}`,
                filters: modelBreakdownFilters,
                onFiltersChange: setModelBreakdownFilters,
              }}
            />
          ) : (
            <div className='loading'>No cost data available.</div>
          )}
        </div>
      )}

      {tab === 'ai-calls' && (
        <div className='ai-calls-section'>
          {aiCallsLoading ? (
            <div className='loading'>Loading AI phone call data...</div>
          ) : aiCallsData ? (
            <AiCallsView data={aiCallsData} />
          ) : (
            <div className='loading'>No AI call data available.</div>
          )}
        </div>
      )}

      {tab === 'browser-mfa' && (
        <div className='browser-mfa-section'>
          {browserMfaLoading ? (
            <div className='loading'>Loading browser &amp; MFA data...</div>
          ) : browserMfaData ? (
            <BrowserMfaView data={browserMfaData} />
          ) : (
            <div className='loading'>No browser session data available.</div>
          )}
        </div>
      )}

      {tab === 'user-lookup' && <PlatformStatsUserLookup />}

      {tab === 'ops-workers' && <PlatformStatsOpsWorkers />}

      {tab === 'campaign-codes' && <PlatformStatsCampaignCodes />}
      {tab === 'feature-flags' && <PlatformStatsFeatureFlags />}

      {tab === 'user-experience' && <PlatformStatsUserExperience />}

      {tab === 'payments' && <PlatformStatsPayments />}
    </div>
  );
};

// ─── Outcomes view ─────────────────────────────────────────────────────────

const formatPct = (v: number | null) =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const formatMinutes = (m: number) => {
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m < 1) return `${Math.round(m * 60)}s`;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

interface OutcomesViewProps {
  outcomes: TaskOutcomesResponse;
  /** Window passed through to the drill-down endpoint so it queries the same range. */
  dateRange: DateRange;
  /** Optional user ID filter for drill-down queries. */
  filterUserId: string | null;
  /** Active task category toggles — forwarded to the drill-down endpoint. */
  taskCategories: {
    user_triggered: boolean;
    routine: boolean;
    internal: boolean;
  };
  /** Which breakdown axis is active. */
  breakdownAxis: BreakdownAxis;
  /** Selected domain-level task type filter (null = all). */
  selectedTaskType: string | null;
  /** Task deepdive data (task type + approach breakdown). */
  deepDive: TaskDeepDiveResponse | null;
  deepDiveLoading: boolean;
}

const OutcomesView: FC<OutcomesViewProps> = ({
  outcomes,
  dateRange: range,
  filterUserId,
  taskCategories,
  selectedTaskType,
  breakdownAxis,
  deepDive,
  deepDiveLoading,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const [chartMode, setChartMode] = useState<'volume' | 'pct'>('volume');

  // Drill-down modal state. `selectedKind` doubles as the "modal open"
  // flag: null = closed. We deliberately reset `drill` whenever the
  // selected kind changes so a stale payload from a previous bucket
  // can't flash for a frame.
  const [selectedKind, setSelectedKind] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillDownResponse | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);
  const [themesLoading, setThemesLoading] = useState(false);

  // Track the pending requestId so the Pusher handler can match it.
  const pendingRequestId = useRef<string | null>(null);

  // Listen for the `outcome-themes-ready` Pusher event on the user channel.
  const userChannel = pusherContext?.userChannel;
  useEffect(() => {
    if (!userChannel) return;
    const handler = (data: {
      requestId: string;
      outcomeKind: string;
      themes: DrillDownTheme[] | null;
    }) => {
      if (data.requestId !== pendingRequestId.current) return;
      pendingRequestId.current = null;
      setThemesLoading(false);

      setDrill((prev) => {
        if (!prev) return prev;

        // Build per-thread theme map so the row "Themes" column updates.
        const themesByThread: Record<string, string[]> = {};
        if (data.themes) {
          for (const t of data.themes) {
            for (const tid of t.threadIds) {
              if (!themesByThread[tid]) themesByThread[tid] = [];
              themesByThread[tid].push(t.name);
            }
          }
        }

        return {
          ...prev,
          themes: data.themes,
          rows: prev.rows.map((r) => ({
            ...r,
            themes: themesByThread[r.threadId] || r.themes,
          })),
        };
      });
    };

    userChannel.bind('outcome-themes-ready', handler);
    return () => {
      userChannel.unbind('outcome-themes-ready', handler);
    };
  }, [userChannel]);

  const openDrillDown = useCallback(
    async (kind: string, axis: BreakdownAxis = 'outcome_kind') => {
      setSelectedKind(kind);
      setDrill(null);
      setDrillError(null);
      setDrillLoading(true);
      setThemesLoading(false);
      pendingRequestId.current = null;
      try {
        const token = await getAccessTokenSilently();
        const bucketParam =
          axis === 'quality_score'
            ? `breakdownAxis=quality_score&qualityScore=${kind}`
            : `outcomeKind=${kind}`;
        const res = await api.get<DrillDownResponse>(
          `/platform-stats/task-outcomes/drill-down?${bucketParam}&${rangeToQuery(range)}${userFilterParam(filterUserId)}${taskCategoriesParam(taskCategories)}${taskTypeParam(selectedTaskType)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) {
          setDrill(res.data);
          // If the backend enqueued a theme clustering job, show loading.
          if (res.data.requestId) {
            pendingRequestId.current = res.data.requestId;
            setThemesLoading(true);
          }
        } else {
          setDrillError('Failed to load drill-down');
        }
      } catch {
        setDrillError('Failed to load drill-down');
      } finally {
        setDrillLoading(false);
      }
    },
    [
      getAccessTokenSilently,
      range,
      filterUserId,
      taskCategories,
      selectedTaskType,
    ],
  );

  const closeDrillDown = useCallback(() => {
    setSelectedKind(null);
    setDrill(null);
    setDrillError(null);
    setDrillLoading(false);
    setThemesLoading(false);
    pendingRequestId.current = null;
  }, []);

  // ESC closes the modal. We attach the listener only while the modal
  // is open so we don't fight other ESC handlers in the page.
  useEffect(() => {
    if (!selectedKind) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrillDown();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedKind, closeDrillDown]);

  const isHourly = outcomes.granularity === 'hour';

  const formatBucketLabel = (raw: string) => {
    if (isHourly) {
      // raw = "2026-06-08 14:00:00" → "14:00"
      const match = raw.match(/(\d{2}):(\d{2}):\d{2}$/);
      return match ? `${match[1]}:${match[2]}` : raw;
    }
    return formatLabel(raw);
  };

  const labels = outcomes.days.map(formatBucketLabel);

  // Determine which series keys, labels, and colors to use based on the active axis.
  // Exclude 'unreviewed' from the chart — it's shown in summary tiles only.
  const chartKeys: string[] =
    breakdownAxis === 'quality_score'
      ? (QUALITY_SCORE_ORDER.filter(
          (k) => k !== 'unreviewed',
        ) as unknown as string[])
      : (TERMINAL_OUTCOMES as unknown as string[]);
  const metaLookup = (key: string) =>
    breakdownAxis === 'quality_score'
      ? (QUALITY_SCORE_META[key as QualityScore] ?? {
          label: key,
          color: '#ccc',
          description: '',
        })
      : (OUTCOME_META[key as OutcomeKind] ?? {
          label: key,
          color: '#ccc',
          description: '',
        });

  // ── Volume view: stacked bar chart ─────────────────────────────────
  const volumeData = {
    labels,
    datasets: chartKeys.map((key) => ({
      label: metaLookup(key).label,
      data: outcomes.series[key] ?? [],
      backgroundColor: metaLookup(key).color,
      borderRadius: 2,
      barPercentage: 0.8,
      stack: 'outcomes',
    })),
  };

  const volumeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: isHourly ? 24 : 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  // ── % over time view: line chart with per-bucket percentages ───────
  const bucketTotals = outcomes.days.map((_, i) =>
    chartKeys.reduce(
      (sum, key) => sum + ((outcomes.series[key] ?? [])[i] || 0),
      0,
    ),
  );

  const pctData = {
    labels,
    datasets: chartKeys.map((key) => ({
      label: metaLookup(key).label,
      data: (outcomes.series[key] ?? []).map((v, i) =>
        bucketTotals[i] > 0 ? +((v / bucketTotals[i]) * 100).toFixed(1) : 0,
      ),
      borderColor: metaLookup(key).color,
      backgroundColor: 'transparent',
      tension: 0.3,
      pointRadius: 2,
      borderWidth: 2,
    })),
  };

  const pctOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: isHourly ? 24 : 15,
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          callback: (v: any) => `${v}%`,
        },
      },
    },
  };

  // Group durations by outcome for quick lookup.
  const durationsByKind = new Map(
    outcomes.durations.map((d) => [d.outcomeKind, d]),
  );

  // ── Summary tile rendering ──────────────────────────────────────────
  const renderSummaryTiles = () => {
    if (breakdownAxis === 'quality_score') {
      const t = outcomes.totals;
      const reviewed = Number(t.reviewed ?? 0);
      const total =
        Number(t.good ?? 0) +
        Number(t.ineffective ?? 0) +
        Number(t.sloppy ?? 0) +
        Number(t.harmful ?? 0) +
        Number(t.unreviewed ?? 0);
      const goodRate = t.goodRate != null ? t.goodRate : null;
      const stuckTotal =
        Number(t.waitingOnUser ?? 0) +
        Number(t.waitingOnThirdParty ?? 0) +
        Number(t.inFlight ?? 0);
      const qPctOf = (count: number) =>
        reviewed > 0 ? `${((count / reviewed) * 100).toFixed(1)}%` : '—';
      return (
        <div className='outcomes-summary'>
          <div className='summary-tile'>
            <div className='tile-label'>
              <span className='dot green' /> Live now
            </div>
            <div className='tile-value'>{stuckTotal}</div>
            <div className='tile-sub'>
              {Number(t.waitingOnUser ?? 0)} waiting on user ·{' '}
              {Number(t.waitingOnThirdParty ?? 0)} on 3rd party ·{' '}
              {Number(t.inFlight ?? 0)} in flight
            </div>
          </div>
          <div className='summary-tile'>
            <div className='tile-label'>Total tasks</div>
            <div className='tile-value'>{total}</div>
            <div className='tile-sub'>{rangeLabel(range)}</div>
          </div>
          <div className='summary-tile'>
            <div className='tile-label'>
              <span className='dot green' /> Good rate
            </div>
            <div className='tile-value'>
              {goodRate != null ? `${(goodRate * 100).toFixed(1)}%` : '—'}
            </div>
            <div className='tile-sub'>
              {Number(t.good ?? 0)} of {reviewed} reviewed
            </div>
          </div>
          <div
            className={`summary-tile ${Number(t.ineffective ?? 0) > 0 ? 'clickable' : ''}`}
            onClick={
              Number(t.ineffective ?? 0) > 0
                ? () => openDrillDown('ineffective', 'quality_score')
                : undefined
            }
            title={
              Number(t.ineffective ?? 0) > 0
                ? 'Click to see why these tasks were scored as ineffective'
                : undefined
            }
          >
            <div className='tile-label'>
              <span className='dot orange' /> Ineffective
            </div>
            <div className='tile-value'>
              {qPctOf(Number(t.ineffective ?? 0))}
            </div>
            <div className='tile-sub'>
              {Number(t.ineffective ?? 0)} task
              {Number(t.ineffective ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
          <div
            className={`summary-tile ${Number(t.sloppy ?? 0) > 0 ? 'clickable' : ''}`}
            onClick={
              Number(t.sloppy ?? 0) > 0
                ? () => openDrillDown('sloppy', 'quality_score')
                : undefined
            }
            title={
              Number(t.sloppy ?? 0) > 0
                ? 'Click to see why these tasks were scored as sloppy'
                : undefined
            }
          >
            <div className='tile-label'>
              <span className='dot orange' /> Sloppy
            </div>
            <div className='tile-value'>{qPctOf(Number(t.sloppy ?? 0))}</div>
            <div className='tile-sub'>
              {Number(t.sloppy ?? 0)} task
              {Number(t.sloppy ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
          <div
            className={`summary-tile ${Number(t.harmful ?? 0) > 0 ? 'clickable' : ''}`}
            onClick={
              Number(t.harmful ?? 0) > 0
                ? () => openDrillDown('harmful', 'quality_score')
                : undefined
            }
            title={
              Number(t.harmful ?? 0) > 0
                ? 'Click to see why these tasks were scored as harmful'
                : undefined
            }
          >
            <div className='tile-label'>
              <span className='dot red' /> Harmful
            </div>
            <div className='tile-value'>{qPctOf(Number(t.harmful ?? 0))}</div>
            <div className='tile-sub'>
              {Number(t.harmful ?? 0)} task
              {Number(t.harmful ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
          <div className='summary-tile'>
            <div className='tile-label'>Unreviewed</div>
            <div className='tile-value'>{Number(t.unreviewed ?? 0)}</div>
            <div className='tile-sub'>pending quality review</div>
          </div>
        </div>
      );
    }

    // outcome_kind axis (default)
    const totalTerminal =
      Number(outcomes.totals.classified ?? 0) +
      Number(outcomes.totals.unclassified ?? 0);
    const stuckTotal =
      Number(outcomes.totals.waitingOnUser ?? 0) +
      Number(outcomes.totals.waitingOnThirdParty ?? 0) +
      Number(outcomes.totals.inFlight ?? 0);
    const windowTotal = totalTerminal + stuckTotal;
    const classified = Number(outcomes.totals.classified ?? 0);
    const pctOf = (count: number) =>
      classified > 0 ? `${((count / classified) * 100).toFixed(1)}%` : '—';

    return (
      <div className='outcomes-summary'>
        <div className='summary-tile'>
          <div className='tile-label'>
            <span className='dot green' /> Live now
          </div>
          <div className='tile-value'>{stuckTotal}</div>
          <div className='tile-sub'>
            {Number(outcomes.totals.waitingOnUser ?? 0)} waiting on user ·{' '}
            {Number(outcomes.totals.waitingOnThirdParty ?? 0)} on 3rd party ·{' '}
            {Number(outcomes.totals.inFlight ?? 0)} in flight
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Total tasks</div>
          <div className='tile-value'>{windowTotal}</div>
          <div className='tile-sub'>{rangeLabel(range)}</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Delivered (Complete)</div>
          <div className='tile-value'>
            {classified > 0
              ? `${((Number(outcomes.totals.delivered ?? 0) / classified) * 100).toFixed(1)}%`
              : '—'}
          </div>
          <div className='tile-sub'>
            {Number(outcomes.totals.delivered ?? 0)} delivered
          </div>
        </div>
        <div
          className={`summary-tile ${Number(outcomes.totals.partialDelivery ?? 0) > 0 ? 'clickable' : ''}`}
          onClick={
            Number(outcomes.totals.partialDelivery ?? 0) > 0
              ? () => openDrillDown('partial_delivery')
              : undefined
          }
          title={
            Number(outcomes.totals.partialDelivery ?? 0) > 0
              ? 'Drill into partial deliveries'
              : undefined
          }
        >
          <div className='tile-label'>
            <span className='dot green' /> Delivered (Partial)
          </div>
          <div className='tile-value'>
            {pctOf(Number(outcomes.totals.partialDelivery ?? 0))}
          </div>
          <div className='tile-sub'>
            {Number(outcomes.totals.partialDelivery ?? 0)} task
            {Number(outcomes.totals.partialDelivery ?? 0) === 1 ? '' : 's'}
          </div>
        </div>
        <div
          className={`summary-tile ${Number(outcomes.totals.informedUnable ?? 0) > 0 ? 'clickable' : ''}`}
          onClick={
            Number(outcomes.totals.informedUnable ?? 0) > 0
              ? () => openDrillDown('informed_unable')
              : undefined
          }
          title={
            Number(outcomes.totals.informedUnable ?? 0) > 0
              ? 'Drill into informed unable'
              : undefined
          }
        >
          <div className='tile-label'>
            <span className='dot green-light' /> Delivered (Unable)
          </div>
          <div className='tile-value'>
            {pctOf(Number(outcomes.totals.informedUnable ?? 0))}
          </div>
          <div className='tile-sub'>
            {Number(outcomes.totals.informedUnable ?? 0)} task
            {Number(outcomes.totals.informedUnable ?? 0) === 1 ? '' : 's'}
          </div>
        </div>
        <div
          className={`summary-tile ${Number(outcomes.totals.cancelled ?? 0) > 0 ? 'clickable' : ''}`}
          onClick={
            Number(outcomes.totals.cancelled ?? 0) > 0
              ? () => openDrillDown('cancelled')
              : undefined
          }
          title={
            Number(outcomes.totals.cancelled ?? 0) > 0
              ? 'Drill into cancelled tasks'
              : undefined
          }
        >
          <div className='tile-label'>
            <span className='dot yellow' /> Cancelled
          </div>
          <div className='tile-value'>
            {pctOf(Number(outcomes.totals.cancelled ?? 0))}
          </div>
          <div className='tile-sub'>
            {Number(outcomes.totals.cancelled ?? 0)} task
            {Number(outcomes.totals.cancelled ?? 0) === 1 ? '' : 's'}
          </div>
        </div>
        <div
          className={`summary-tile ${Number(outcomes.totals.failed ?? 0) > 0 ? 'clickable' : ''}`}
          onClick={
            Number(outcomes.totals.failed ?? 0) > 0
              ? () => openDrillDown('failed')
              : undefined
          }
          title={
            Number(outcomes.totals.failed ?? 0) > 0
              ? 'Drill into failed tasks'
              : undefined
          }
        >
          <div className='tile-label'>
            <span className='dot red' /> Failed
          </div>
          <div className='tile-value'>
            {pctOf(Number(outcomes.totals.failed ?? 0))}
          </div>
          <div className='tile-sub'>
            {Number(outcomes.totals.failed ?? 0)} task
            {Number(outcomes.totals.failed ?? 0) === 1 ? '' : 's'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='outcomes-view'>
      {renderSummaryTiles()}

      <div className='chart-card wide outcomes-chart'>
        <div className='chart-header'>
          <div className='chart-title-block'>
            <div className='chart-title'>
              {chartMode === 'volume'
                ? `${breakdownAxis === 'quality_score' ? 'Quality scores' : 'Outcomes'} per ${isHourly ? 'hour' : 'day'}`
                : `${breakdownAxis === 'quality_score' ? 'Quality score' : 'Outcome'} rates per ${isHourly ? 'hour' : 'day'}`}
            </div>
            {chartMode === 'pct' && (
              <div className='chart-subtitle'>
                share of tasks in each bucket
              </div>
            )}
          </div>
          <div className='chart-mode-toggle'>
            <button
              className={`toggle-btn ${chartMode === 'volume' ? 'active' : ''}`}
              onClick={() => setChartMode('volume')}
            >
              Volume
            </button>
            <button
              className={`toggle-btn ${chartMode === 'pct' ? 'active' : ''}`}
              onClick={() => setChartMode('pct')}
            >
              % over time
            </button>
          </div>
        </div>
        {chartMode === 'volume' ? (
          <Bar data={volumeData} options={volumeOptions} />
        ) : (
          <Line data={pctData} options={pctOptions} />
        )}
      </div>

      {/* Tasks Deepdive — right after the chart */}
      {deepDiveLoading ? (
        <div className='loading' style={{ marginTop: 16 }}>
          Loading tasks deepdive...
        </div>
      ) : deepDive ? (
        <TasksDeepDiveView data={deepDive} breakdownAxis={breakdownAxis} />
      ) : null}

      {selectedKind && (
        <DrillDownModal
          outcomeKind={selectedKind}
          windowLabel={rangeLabel(range)}
          loading={drillLoading}
          themesLoading={themesLoading}
          error={drillError}
          data={drill}
          onClose={closeDrillDown}
        />
      )}
    </div>
  );
};

// ─── Approach Outcomes View ───────────────────────────────────────────────────

interface ApproachOutcomesViewProps {
  data: ApproachOutcomesResponse;
}

const ROLE_ORDER = [
  'primary_driver',
  'supporting',
  'attempted_failed',
  'blocked_task',
];

const ApproachOutcomesView: FC<ApproachOutcomesViewProps> = ({ data }) => {
  const {
    summary,
    effectiveness,
    primaryDrivers,
    failureAttribution,
    recoveryPatterns,
  } = data;

  const coverageRate =
    summary.totalTerminal > 0
      ? ((summary.totalWithApproaches / summary.totalTerminal) * 100).toFixed(1)
      : '0';

  // Top primary driver
  const topDriver = primaryDrivers.length > 0 ? primaryDrivers[0] : null;

  // Top failure cause
  const topBlocker =
    failureAttribution.length > 0 ? failureAttribution[0] : null;

  // ── Effectiveness horizontal stacked bar ────────────────────────────
  const effectivenessData = {
    labels: effectiveness.map((e) => APPROACH_LABELS[e.approach] || e.approach),
    datasets: ROLE_ORDER.map((role) => ({
      label: ROLE_LABELS[role] || role,
      data: effectiveness.map((e) => e.roles[role] || 0),
      backgroundColor: ROLE_COLORS[role],
      borderRadius: 2,
      barPercentage: 0.7,
    })),
  };

  const effectivenessOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Approach Effectiveness (role distribution)',
        font: {
          family: 'Instrument Sans, sans-serif',
          size: 14,
          weight: 600 as const,
        },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#444',
        },
      },
    },
  };

  // ── Primary Drivers pie ─────────────────────────────────────────────
  const driverPieColors = [
    'rgba(34, 197, 94, 0.8)',
    'rgba(59, 130, 246, 0.7)',
    'rgba(168, 85, 247, 0.7)',
    'rgba(249, 115, 22, 0.7)',
    'rgba(236, 72, 153, 0.7)',
    'rgba(20, 184, 166, 0.7)',
    'rgba(234, 179, 8, 0.7)',
    'rgba(107, 114, 128, 0.6)',
  ];

  const driverPieData = {
    labels: primaryDrivers.map(
      (d) => APPROACH_LABELS[d.approach] || d.approach,
    ),
    datasets: [
      {
        data: primaryDrivers.map((d) => d.count),
        backgroundColor: primaryDrivers.map(
          (_, i) => driverPieColors[i % driverPieColors.length],
        ),
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 8,
        },
      },
    },
  };

  // ── Failure Attribution pie ─────────────────────────────────────────
  const failurePieColors = [
    'rgba(239, 68, 68, 0.8)',
    'rgba(251, 146, 60, 0.7)',
    'rgba(234, 179, 8, 0.7)',
    'rgba(107, 114, 128, 0.6)',
    'rgba(168, 85, 247, 0.6)',
    'rgba(59, 130, 246, 0.6)',
  ];

  const failurePieData = {
    labels: failureAttribution.map(
      (d) => APPROACH_LABELS[d.approach] || d.approach,
    ),
    datasets: [
      {
        data: failureAttribution.map((d) => d.count),
        backgroundColor: failureAttribution.map(
          (_, i) => failurePieColors[i % failurePieColors.length],
        ),
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  return (
    <div className='approach-outcomes-view' style={{ marginTop: 40 }}>
      <h3
        style={{
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 16,
          fontFamily: 'Instrument Sans, sans-serif',
        }}
      >
        Approach Outcomes
      </h3>

      {/* Effectiveness stacked bar */}
      {effectiveness.length > 0 && (
        <div
          className='chart-card wide'
          style={{ height: Math.max(200, effectiveness.length * 44 + 80) }}
        >
          <Bar data={effectivenessData} options={effectivenessOptions} />
        </div>
      )}

      {/* Recovery Patterns table */}
      {recoveryPatterns.length > 0 && (
        <div className='outcomes-breakdown' style={{ marginTop: 20 }}>
          <div className='breakdown-title'>
            Recovery Patterns (failed approach → successful fallback)
          </div>
          <table className='outcomes-table'>
            <thead>
              <tr>
                <th>Failed Approach</th>
                <th>Recovered Via</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {recoveryPatterns.map((r) => (
                <tr key={`${r.failedApproach}-${r.recoveredVia}`}>
                  <td>
                    {APPROACH_LABELS[r.failedApproach] || r.failedApproach}
                  </td>
                  <td>{APPROACH_LABELS[r.recoveredVia] || r.recoveredVia}</td>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Tasks Deepdive View ──────────────────────────────────────────────────────

interface TasksDeepDiveViewProps {
  data: TaskDeepDiveResponse;
  breakdownAxis: BreakdownAxis;
}

const OUTCOME_COLORS_DD: Record<string, string> = {
  delivered: '#16a34a',
  partialDelivery: '#4ade80',
  informedUnable: '#86efac',
  failed: '#ef4444',
  cancelled: '#eab308',
};

const QUALITY_COLORS_DD: Record<string, string> = {
  good: '#22c55e',
  ineffective: '#facc15',
  sloppy: '#f97316',
  harmful: '#ef4444',
};

const TasksDeepDiveView: FC<TasksDeepDiveViewProps> = ({
  data,
  breakdownAxis,
}) => {
  const [tab, setTab] = useState<'type' | 'approach'>('type');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const totalVolume =
    tab === 'type'
      ? data.taskTypes.reduce((s, r) => s + r.volume, 0)
      : data.approaches.reduce((s, r) => s + r.volume, 0);

  const toggleRow = (key: string) =>
    setExpandedRow((prev) => (prev === key ? null : key));

  const pct = (n: number, d: number) =>
    d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—';

  const completionRate = (row: TaskTypeRow | ApproachRow) => {
    if (breakdownAxis === 'quality_score') {
      return pct(row.good ?? 0, row.volume);
    }
    return pct((row.delivered ?? 0) + (row.partialDelivery ?? 0), row.volume);
  };

  const failureRate = (row: TaskTypeRow | ApproachRow) => {
    if (breakdownAxis === 'quality_score') {
      return pct((row.sloppy ?? 0) + (row.harmful ?? 0), row.volume);
    }
    return pct(row.failed ?? 0, row.volume);
  };

  const outcomeBar = (row: TaskTypeRow | ApproachRow) => {
    const segments =
      breakdownAxis === 'quality_score'
        ? [
            {
              key: 'good',
              value: row.good ?? 0,
              color: QUALITY_COLORS_DD.good,
            },
            {
              key: 'ineffective',
              value: row.ineffective ?? 0,
              color: QUALITY_COLORS_DD.ineffective,
            },
            {
              key: 'sloppy',
              value: row.sloppy ?? 0,
              color: QUALITY_COLORS_DD.sloppy,
            },
            {
              key: 'harmful',
              value: row.harmful ?? 0,
              color: QUALITY_COLORS_DD.harmful,
            },
          ]
        : [
            {
              key: 'delivered',
              value: row.delivered ?? 0,
              color: OUTCOME_COLORS_DD.delivered,
            },
            {
              key: 'partialDelivery',
              value: row.partialDelivery ?? 0,
              color: OUTCOME_COLORS_DD.partialDelivery,
            },
            {
              key: 'informedUnable',
              value: row.informedUnable ?? 0,
              color: OUTCOME_COLORS_DD.informedUnable,
            },
            {
              key: 'failed',
              value: row.failed ?? 0,
              color: OUTCOME_COLORS_DD.failed,
            },
            {
              key: 'cancelled',
              value: row.cancelled ?? 0,
              color: OUTCOME_COLORS_DD.cancelled,
            },
          ];
    return (
      <div className='dd-outcome-bar'>
        {segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.key}
              className='dd-outcome-segment'
              style={{
                width: `${(s.value / row.volume) * 100}%`,
                backgroundColor: s.color,
              }}
              title={`${s.key}: ${s.value}`}
            />
          ) : null,
        )}
      </div>
    );
  };

  // Render approach split for a task type's expanded row
  const renderApproachSplit = (approaches: TaskTypeRow['approaches']) => {
    const total = approaches.reduce((s, a) => s + a.volume, 0);
    return (
      <div className='dd-expanded-content'>
        <div className='dd-expanded-header'>
          {breakdownAxis === 'quality_score'
            ? 'Approach Split & Good Rate'
            : 'Approach Split & Completion'}
        </div>
        {approaches.map((a) => (
          <div key={a.approach} className='dd-approach-row'>
            <div className='dd-approach-name'>
              {APPROACH_LABELS[a.approach] || a.approach}
            </div>
            <div className='dd-approach-bar-wrap'>
              <div
                className='dd-approach-bar'
                style={{ width: `${(a.volume / total) * 100}%` }}
              >
                <div
                  className='dd-approach-bar-fill'
                  style={{
                    width: `${a.volume > 0 ? (a.delivered / a.volume) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
            <div className='dd-approach-pct'>{pct(a.volume, total)}</div>
            <div className='dd-approach-completion'>
              {pct(a.delivered, a.volume)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render sub-category breakdown for a task type's expanded row
  const renderSubCategoryBreakdown = (
    subCategories: NonNullable<TaskTypeRow['subCategories']>,
    parentVolume: number,
  ) => {
    return (
      <div className='dd-expanded-content'>
        <div className='dd-expanded-header'>Subtype Breakdown</div>
        {subCategories.map((sc) => (
          <div key={sc.slug} className='dd-approach-row'>
            <div className='dd-approach-name'>{sc.label}</div>
            <div className='dd-approach-bar-wrap'>
              <div
                className='dd-approach-bar'
                style={{ width: `${(sc.volume / parentVolume) * 100}%` }}
              >
                <div
                  className='dd-approach-bar-fill'
                  style={{
                    width: `${sc.volume > 0 ? (sc.delivered / sc.volume) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
            <div className='dd-approach-pct'>{sc.volume}</div>
            <div className='dd-approach-completion'>
              {pct(sc.delivered, sc.volume)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render combination breakdown for an approach's expanded row
  const renderCombinations = (
    approach: string,
    combinations: ApproachRow['combinations'],
  ) => {
    const total = combinations.reduce((s, c) => s + c.volume, 0);
    const approachLabel = APPROACH_LABELS[approach] || approach;

    return (
      <div className='dd-expanded-content'>
        <div className='dd-expanded-header'>
          Combinations with other approaches
        </div>
        <div className='dd-expanded-subtext'>
          How often <strong>{approachLabel}</strong> is used on its own vs.
          paired with another approach on the same task, with the{' '}
          {breakdownAxis === 'quality_score' ? 'good rate' : 'completion rate'}{' '}
          for each combination.
        </div>
        <table className='dd-combo-table'>
          <thead>
            <tr>
              <th>Combination</th>
              <th>Volume</th>
              <th>Share</th>
              <th>
                {breakdownAxis === 'quality_score' ? 'Good Rate' : 'Completion'}
              </th>
            </tr>
          </thead>
          <tbody>
            {combinations.map((c) => {
              const approaches = c.combination.split(',');
              const isSolo = approaches.length === 1;
              const label = isSolo
                ? `${approachLabel} only`
                : approaches.map((a) => APPROACH_LABELS[a] || a).join(' + ');
              return (
                <tr key={c.combination}>
                  <td>
                    {isSolo && <span className='dd-solo-badge'>solo</span>}
                    {label}
                  </td>
                  <td>
                    <div className='dd-combo-bar-wrap'>
                      <div
                        className='dd-combo-bar'
                        style={{
                          width: `${total > 0 ? (c.volume / total) * 100 : 0}%`,
                        }}
                      />
                      <span className='dd-combo-bar-label'>{c.volume}</span>
                    </div>
                  </td>
                  <td>{pct(c.volume, total)}</td>
                  <td className='dd-completion-cell'>
                    {pct(c.delivered, c.volume)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (data.taskTypes.length === 0 && data.approaches.length === 0) return null;

  return (
    <div className='tasks-deepdive'>
      <div className='dd-header'>
        <h3 className='dd-title'>Tasks Deepdive</h3>
        <div className='dd-tab-toggle'>
          <button
            className={tab === 'type' ? 'active' : ''}
            onClick={() => {
              setTab('type');
              setExpandedRow(null);
            }}
          >
            Task Type
          </button>
          <button
            className={tab === 'approach' ? 'active' : ''}
            onClick={() => {
              setTab('approach');
              setExpandedRow(null);
            }}
          >
            Task Approach
          </button>
        </div>
      </div>

      <div className='dd-section-info'>
        <strong>
          {tab === 'type' ? 'Task type breakdown' : 'Task approach breakdown'}
        </strong>
        <span>
          {tab === 'type'
            ? breakdownAxis === 'quality_score'
              ? 'Volume, quality mix, good rate and issue rate per category.'
              : 'Volume, outcome mix, completion and failure rate per category.'
            : breakdownAxis === 'quality_score'
              ? 'Volume, quality mix, good rate and issue rate per execution route.'
              : 'Volume, outcome mix, completion and failure rate per execution route.'}
        </span>
      </div>

      <table className='dd-table'>
        <thead>
          <tr>
            <th>{tab === 'type' ? 'Task Type' : 'Approach'}</th>
            <th>Volume</th>
            <th>
              {breakdownAxis === 'quality_score'
                ? 'Quality Mix'
                : 'Outcome Mix'}
            </th>
            <th>
              {breakdownAxis === 'quality_score' ? 'Good Rate' : 'Completion'}
            </th>
            <th>
              {breakdownAxis === 'quality_score'
                ? 'Issue Rate'
                : 'Failure Rate'}
            </th>
          </tr>
        </thead>
        <tbody>
          {tab === 'type'
            ? data.taskTypes.map((row) => {
                const key = row.category;
                const isExpanded = expandedRow === key;
                return (
                  <Fragment key={key}>
                    <tr
                      className={`dd-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleRow(key)}
                    >
                      <td className='dd-name-cell'>
                        <span className='dd-expand-icon'>
                          {isExpanded ? '▾' : '▸'}
                        </span>
                        {TASK_CATEGORY_LABELS[key] || key}
                      </td>
                      <td>
                        <span className='dd-volume'>{row.volume}</span>
                        <span className='dd-pct'>
                          {pct(row.volume, totalVolume)}
                        </span>
                      </td>
                      <td>{outcomeBar(row)}</td>
                      <td className='dd-completion-cell'>
                        {completionRate(row)}
                      </td>
                      <td className='dd-failure-cell'>{failureRate(row)}</td>
                    </tr>
                    {isExpanded &&
                      (row.approaches.length > 0 ||
                        (row.subCategories &&
                          row.subCategories.length > 0)) && (
                        <tr className='dd-expanded-row'>
                          <td colSpan={5}>
                            <div className='dd-expanded-columns'>
                              {row.approaches.length > 0 &&
                                renderApproachSplit(row.approaches)}
                              {row.subCategories &&
                                row.subCategories.length > 0 &&
                                renderSubCategoryBreakdown(
                                  row.subCategories,
                                  row.volume,
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                  </Fragment>
                );
              })
            : data.approaches.map((row) => {
                const key = row.approach;
                const isExpanded = expandedRow === key;
                return (
                  <Fragment key={key}>
                    <tr
                      className={`dd-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleRow(key)}
                    >
                      <td className='dd-name-cell'>
                        <span className='dd-expand-icon'>
                          {isExpanded ? '▾' : '▸'}
                        </span>
                        {APPROACH_LABELS[key] || key}
                      </td>
                      <td>
                        <span className='dd-volume'>{row.volume}</span>
                        <span className='dd-pct'>
                          {pct(row.volume, totalVolume)}
                        </span>
                      </td>
                      <td>{outcomeBar(row)}</td>
                      <td className='dd-completion-cell'>
                        {completionRate(row)}
                      </td>
                      <td className='dd-failure-cell'>{failureRate(row)}</td>
                    </tr>
                    {isExpanded && row.combinations.length > 0 && (
                      <tr className='dd-expanded-row'>
                        <td colSpan={5}>
                          {renderCombinations(row.approach, row.combinations)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Drill-down modal ──────────────────────────────────────────────────────

interface DrillDownModalProps {
  outcomeKind: string;
  /** Human-readable description of the active window, e.g. "last 30 days" or "2026-04-15 → 2026-04-22". */
  windowLabel: string;
  loading: boolean;
  /** True while the BullMQ job is still clustering themes (arrives via Pusher). */
  themesLoading: boolean;
  error: string | null;
  data: DrillDownResponse | null;
  onClose: () => void;
}

const DrillDownModal: FC<DrillDownModalProps> = ({
  outcomeKind,
  windowLabel,
  loading,
  themesLoading,
  error,
  data,
  onClose,
}) => {
  const meta = QUALITY_SCORE_META[outcomeKind as QualityScore] ??
    OUTCOME_META[outcomeKind as OutcomeKind] ?? {
      label: outcomeKind,
      color: '#999',
      description: '',
    };

  // Format ISO timestamp into "DD MMM, HH:mm" — short but unambiguous.
  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    });
    const time = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${date}, ${time}`;
  };

  // Format duration between two ISO timestamps in human terms.
  const formatRange = (startIso: string, endIso: string) => {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
      return '—';
    const minutes = (end - start) / 60_000;
    return formatMinutes(minutes);
  };

  return (
    <div
      className='drill-down-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className='drill-down-panel'
        role='dialog'
        aria-modal='true'
        aria-label={`Drill-down for ${meta.label}`}
      >
        <div className='drill-down-header'>
          <div className='drill-down-title'>
            <span className='outcome-dot' style={{ background: meta.color }} />
            <span>{meta.label}</span>
            <span className='drill-down-window'>· {windowLabel}</span>
          </div>
          <button
            type='button'
            className='drill-down-close'
            onClick={onClose}
            aria-label='Close drill-down'
          >
            ×
          </button>
        </div>

        {loading && (
          <div className='drill-down-loading'>Loading drill-down…</div>
        )}

        {error && !loading && <div className='drill-down-error'>{error}</div>}

        {data && !loading && (
          <>
            <div className='drill-down-summary'>
              {data.total} matching task{data.total === 1 ? '' : 's'}
              {data.total >= 500 ? ' (capped at 500 most-recent)' : ''}.
            </div>

            {/* Themes section — only rendered when the backend actually
                ran clustering. For delivered / non-terminal / unclassified
                buckets the backend short-circuits and we show a helper
                blurb in place of an empty themes list. */}
            {data.themesSkipped ? (
              <div className='drill-down-themes-skipped'>
                Theme classification is skipped for this bucket — it adds little
                signal here. Use the row list below instead.
              </div>
            ) : themesLoading ? (
              <div className='drill-down-themes-loading'>
                Classifying themes… this may take 10–30 s.
              </div>
            ) : data.themes && data.themes.length > 0 ? (
              <div className='drill-down-themes'>
                <div className='drill-down-section-title'>
                  Themes (LLM-generated)
                </div>
                <ul className='drill-down-theme-list'>
                  {data.themes.map((t) => (
                    <li key={t.name}>
                      <div className='theme-name'>
                        {t.name}{' '}
                        <span className='theme-count'>
                          ({t.threadIds.length})
                        </span>
                      </div>
                      <div className='theme-description'>{t.description}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : data.total > 0 ? (
              <div className='drill-down-themes-skipped'>
                Theme classification did not return results.
              </div>
            ) : null}

            {/* Row list — privacy-conscious: no descriptions / summaries. */}
            <div className='drill-down-rows'>
              <div className='drill-down-section-title'>Tasks</div>
              {data.rows.length === 0 ? (
                <div className='drill-down-empty'>
                  No tasks in this bucket for the selected window.
                </div>
              ) : (
                <table className='drill-down-table'>
                  <thead>
                    <tr>
                      <th>Thread</th>
                      <th>User</th>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Duration</th>
                      <th>Themes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.threadId}>
                        <td className='mono thread-id-cell'>
                          <span
                            className='thread-id'
                            title='Click to copy'
                            onClick={() => {
                              if (navigator.clipboard?.writeText) {
                                navigator.clipboard
                                  .writeText(row.threadId)
                                  .catch(() => {});
                              }
                            }}
                          >
                            {row.threadId}
                          </span>
                        </td>
                        <td>{row.userName || '—'}</td>
                        <td>{formatDateTime(row.startedAt)}</td>
                        <td>{formatDateTime(row.endedAt)}</td>
                        <td>{formatRange(row.startedAt, row.endedAt)}</td>
                        <td>
                          {row.themes.length > 0 ? (
                            <div className='theme-tags'>
                              {row.themes.map((tn) => (
                                <span key={tn} className='theme-tag'>
                                  {tn}
                                </span>
                              ))}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Costs view ───────────────────────────────────────────────────────────────

// Stable colour palette for model names. Hues are spread evenly around the
// wheel so they stay distinguishable even with 5+ models.
const MODEL_COLORS = [
  'rgba(239, 68, 68, 0.75)', // red
  'rgba(59, 130, 246, 0.75)', // blue
  'rgba(34, 197, 94, 0.75)', // green
  'rgba(168, 85, 247, 0.75)', // purple
  'rgba(245, 158, 11, 0.75)', // amber
  'rgba(14, 165, 233, 0.75)', // sky
  'rgba(236, 72, 153, 0.75)', // pink
  'rgba(20, 184, 166, 0.75)', // teal
];

interface CostsViewProps {
  data: StatsResponse;
  dailyCost: ComponentProps<typeof DailyCostChart>;
  modelBreakdown: ComponentProps<typeof ModelBreakdownTable>;
}

const CostsView: FC<CostsViewProps> = ({ data, dailyCost, modelBreakdown }) => {
  const { days, series, llmCosts } = data;
  const labels = days.map(formatLabel);

  // ── Aggregate model stats across the entire window ──────────────────────
  const modelTotals = useMemo(() => {
    const map = new Map<string, { calls: number; cost: number }>();
    for (const dayModels of llmCosts.modelUsage) {
      for (const m of dayModels) {
        const prev = map.get(m.model) ?? { calls: 0, cost: 0 };
        map.set(m.model, {
          calls: prev.calls + m.calls,
          cost: prev.cost + m.cost,
        });
      }
    }
    return Array.from(map.entries())
      .map(([model, stats]) => ({ model, ...stats }))
      .sort((a, b) => b.cost - a.cost);
  }, [llmCosts.modelUsage]);

  const windowTotalCost = llmCosts.totalCost.reduce((a, b) => a + b, 0);
  const windowTotalCalls = modelTotals.reduce((a, m) => a + m.calls, 0);
  const windowTotalTasks = series.tasks.reduce((a, b) => a + b, 0);
  const avgCostPerTask =
    windowTotalTasks > 0 ? windowTotalCost / windowTotalTasks : 0;
  const avgCostPerCall =
    windowTotalCalls > 0 ? windowTotalCost / windowTotalCalls : 0;

  // ── Cache statistics ────────────────────────────────────────────────────
  // Aggregate cache savings across all models. netSavings is computed
  // server-side as cost_without_caching_usd - estimated_cost_usd,
  // populated at write time using per-model pricing.
  const cacheTotals = useMemo(() => {
    let totalInputTokens = 0;
    let totalInputTokensSinceCaching = 0;
    let totalCacheReadTokens = 0;
    let totalCacheWriteTokens = 0;
    let netSavings = 0;
    for (const s of llmCosts.cacheStats ?? []) {
      totalInputTokens += s.inputTokens;
      totalInputTokensSinceCaching += s.inputTokensSinceCaching;
      totalCacheReadTokens += s.cacheReadTokens;
      totalCacheWriteTokens += s.cacheWriteTokens;
      netSavings += s.netSavings;
    }
    // Use inputTokensSinceCaching as denominator so pre-caching rows
    // don't artificially suppress the ratio.
    const hitRatio =
      totalInputTokensSinceCaching > 0
        ? totalCacheReadTokens / totalInputTokensSinceCaching
        : 0;
    return {
      totalInputTokens,
      totalInputTokensSinceCaching,
      totalCacheReadTokens,
      totalCacheWriteTokens,
      hitRatio,
      netSavings,
    };
  }, [llmCosts.cacheStats]);

  // Avg cost per task per day
  const avgCostPerTaskPerDay = days.map((_, i) =>
    series.tasks[i] > 0 ? llmCosts.totalCost[i] / series.tasks[i] : 0,
  );

  return (
    <div className='costs-view'>
      {/* Summary tiles */}
      <div className='costs-summary'>
        <div className='summary-tile'>
          <div className='tile-label'>Total Spend (window)</div>
          <div className='tile-value'>{formatUSD(windowTotalCost)}</div>
          <div className='tile-sub'>
            {windowTotalCalls.toLocaleString()} LLM calls
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Avg Cost per Task</div>
          <div className='tile-value'>{formatUSD(avgCostPerTask)}</div>
          <div className='tile-sub'>
            {windowTotalTasks.toLocaleString()} tasks in window
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Avg Cost per LLM Call</div>
          <div className='tile-value'>{formatUSD(avgCostPerCall)}</div>
        </div>
        {cacheTotals.totalCacheReadTokens > 0 && (
          <div className='summary-tile'>
            <div className='tile-label'>Cache Hit Ratio</div>
            <div className='tile-value'>
              {(cacheTotals.hitRatio * 100).toFixed(1)}%
            </div>
            <div className='tile-sub'>
              {(cacheTotals.totalCacheReadTokens / 1e6).toFixed(1)}M tokens from
              cache
            </div>
          </div>
        )}
        {cacheTotals.netSavings > 0 && (
          <div className='summary-tile'>
            <div className='tile-label'>Cache Savings (net)</div>
            <div className='tile-value'>
              {formatUSD(cacheTotals.netSavings)}
            </div>
            <div className='tile-sub'>
              {windowTotalCost > 0
                ? `${((cacheTotals.netSavings / (windowTotalCost + cacheTotals.netSavings)) * 100).toFixed(1)}% of what spend would be`
                : ''}
            </div>
          </div>
        )}
      </div>

      {/* Trigger cost pie charts */}
      {llmCosts.triggerCosts && (
        <div className='costs-trigger-pies'>
          {(
            [
              { label: 'Past Month', data: llmCosts.triggerCosts.month },
              { label: 'Past Week', data: llmCosts.triggerCosts.week },
              { label: 'Past Day', data: llmCosts.triggerCosts.day },
            ] as const
          ).map(({ label, data: slices }) => {
            const total = slices.reduce((s, r) => s + r.cost, 0);
            return (
              <div className='costs-chart-card pie-card' key={label}>
                <Pie
                  data={{
                    labels: slices.map((s) => s.trigger),
                    datasets: [
                      {
                        data: slices.map((s) => s.cost),
                        backgroundColor: [
                          ...MODEL_COLORS,
                          'rgba(100, 116, 139, 0.75)',
                          'rgba(251, 146, 60, 0.75)',
                          'rgba(99, 102, 241, 0.75)',
                        ],
                        borderWidth: 1,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      title: {
                        display: true,
                        text: `${label} — ${formatUSD(total)}`,
                        font: {
                          family: 'Instrument Sans, sans-serif',
                          size: 14,
                          weight: 600 as const,
                        },
                        color: '#222',
                        padding: { bottom: 8 },
                      },
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          font: {
                            family: 'Instrument Sans, sans-serif',
                            size: 10,
                          },
                          boxWidth: 10,
                          padding: 6,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx: any) => {
                            const val = ctx.raw as number;
                            const pct =
                              total > 0
                                ? ((val / total) * 100).toFixed(1)
                                : '0';
                            return `${ctx.label}: ${formatUSD(val)} (${pct}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Stacked area: daily cost by model / trigger / agent */}
      <DailyCostChart {...dailyCost} />

      {/* Avg cost per task per day */}
      <div className='costs-chart-card'>
        <Line
          data={{
            labels,
            datasets: [
              {
                label: 'Avg Cost per Task',
                data: avgCostPerTaskPerDay,
                borderColor: 'rgb(244, 114, 182)',
                backgroundColor: 'rgba(244, 114, 182, 0.08)',
                fill: true,
                tension: 0.35,
                pointRadius: 2,
              },
            ],
          }}
          options={{
            ...lineChartOptions('Avg Cost per Task per Day ($)'),
            plugins: {
              ...lineChartOptions('Avg Cost per Task per Day ($)').plugins,
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                },
              },
            },
            scales: {
              ...lineChartOptions('Avg Cost per Task per Day ($)').scales,
              y: {
                ...lineChartOptions('Avg Cost per Task per Day ($)').scales.y,
                ticks: {
                  ...lineChartOptions('Avg Cost per Task per Day ($)').scales.y
                    .ticks,
                  callback: (v: any) => formatUSD(v),
                },
              },
            },
          }}
        />
      </div>

      {/* Avg cost per account per day */}
      <div className='costs-chart-card'>
        <Line
          data={{
            labels,
            datasets: [
              {
                label: 'Avg per Account',
                data: llmCosts.avgCostPerUser,
                borderColor: 'rgb(168, 85, 247)',
                backgroundColor: 'rgba(168, 85, 247, 0.08)',
                fill: true,
                tension: 0.35,
                pointRadius: 2,
              },
            ],
          }}
          options={{
            ...lineChartOptions('Avg Cost per Account per Day ($)'),
            plugins: {
              ...lineChartOptions('Avg Cost per Account per Day ($)').plugins,
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                },
              },
            },
            scales: {
              ...lineChartOptions('Avg Cost per Account per Day ($)').scales,
              y: {
                ...lineChartOptions('Avg Cost per Account per Day ($)').scales
                  .y,
                ticks: {
                  ...lineChartOptions('Avg Cost per Account per Day ($)').scales
                    .y.ticks,
                  callback: (v: any) => formatUSD(v),
                },
              },
            },
          }}
        />
      </div>

      {/* Model breakdown table (filterable by trigger / agent type+node) */}
      <ModelBreakdownTable {...modelBreakdown} />
    </div>
  );
};

// ─── AI Calls view ──────────────────────────────────────────────────────────

const CATEGORY_GROUP_META: Record<
  string,
  { label: string; color: string; description: string }
> = {
  success: {
    label: 'Success',
    color: 'rgba(34, 197, 94, 0.75)',
    description: 'Task completed or partially completed',
  },
  task_failure: {
    label: 'Task Failure',
    color: 'rgba(239, 68, 68, 0.75)',
    description: 'Connected but task could not be completed',
  },
  connection_failure: {
    label: 'Connection Failure',
    color: 'rgba(245, 158, 11, 0.75)',
    description: 'Could not reach the recipient (busy, no answer, voicemail)',
  },
  early_termination: {
    label: 'Early Termination',
    color: 'rgba(168, 85, 247, 0.75)',
    description: 'Recipient hung up or refused to engage',
  },
  audio_technical: {
    label: 'Audio / Technical',
    color: 'rgba(14, 165, 233, 0.75)',
    description: 'Technical issue with audio or the bridge',
  },
  other: {
    label: 'Other',
    color: 'rgba(100, 116, 139, 0.75)',
    description: 'Uncategorized or unknown outcome',
  },
  unassessed: {
    label: 'Unassessed',
    color: 'rgba(203, 213, 225, 0.6)',
    description: 'Calls not yet assessed by the specialist',
  },
};

const CATEGORY_GROUP_ORDER = [
  'success',
  'task_failure',
  'connection_failure',
  'early_termination',
  'audio_technical',
  'other',
  'unassessed',
];

const CATEGORY_LABELS: Record<string, string> = {
  carrier_rejected: 'Carrier Rejected',
  number_invalid: 'Number Invalid',
  number_busy: 'Number Busy',
  no_answer: 'No Answer',
  voicemail_detected: 'Voicemail',
  call_timeout: 'Call Timeout',
  recipient_hung_up_immediately: 'Hung Up Immediately',
  recipient_refused_ai: 'Refused AI',
  wrong_person: 'Wrong Person',
  wrong_number: 'Wrong Number',
  ivr_failure: 'IVR Failure',
  recipient_inaudible: 'Recipient Inaudible',
  agent_inaudible: 'Agent Inaudible',
  bridge_crashed: 'Bridge Crashed',
  realtime_api_error: 'Realtime API Error',
  task_success: 'Task Success',
  task_partial: 'Task Partial',
  task_infeasible: 'Task Infeasible',
  task_deferred: 'Task Deferred',
  task_refused: 'Task Refused',
  agent_error: 'Agent Error',
  escalated_to_human: 'Escalated to Human',
  other: 'Other',
};

interface AiCallsViewProps {
  data: AiCallsStatsResponse;
}

const AiCallsView: FC<AiCallsViewProps> = ({ data }) => {
  const labels = data.days.map(formatLabel);
  const { totals } = data;

  // ── Stacked bar: category groups per day ─────────────────────────────────
  const stackedData = {
    labels,
    datasets: CATEGORY_GROUP_ORDER.map((key) => ({
      label: CATEGORY_GROUP_META[key].label,
      data: data.categoryGroupSeries[key] ?? [],
      backgroundColor: CATEGORY_GROUP_META[key].color,
      borderRadius: 2,
      barPercentage: 0.8,
      stack: 'outcomes',
    })),
  };

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Call Outcomes per Day',
        font: {
          family: 'Instrument Sans, sans-serif',
          size: 14,
          weight: 600 as const,
        },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  // ── Response latency histogram ───────────────────────────────────────
  const latencies = data.responseLatencies;
  const bucketSize = 250; // ms
  const maxBucket = Math.max(3000, ...latencies);
  const bucketCount = Math.ceil(maxBucket / bucketSize);
  const bucketCounts = new Array(bucketCount).fill(0);
  for (const ms of latencies) {
    const idx = Math.min(Math.floor(ms / bucketSize), bucketCount - 1);
    bucketCounts[idx]++;
  }
  const histogramLabels = bucketCounts.map(
    (_, i) =>
      `${((i * bucketSize) / 1000).toFixed(1)}–${(((i + 1) * bucketSize) / 1000).toFixed(1)}s`,
  );
  const histogramData = {
    labels: histogramLabels,
    datasets: [
      {
        label: 'Calls',
        data: bucketCounts,
        backgroundColor: 'rgba(59, 130, 246, 0.65)',
        borderRadius: 3,
        barPercentage: 0.9,
      },
    ],
  };
  const histogramOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `First-Turn Response Latency (${latencies.length} calls)`,
        font: {
          family: 'Instrument Sans, sans-serif',
          size: 14,
          weight: 600 as const,
        },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        callbacks: {
          title: (items: any) => items[0]?.label ?? '',
          label: (ctx: any) => `${ctx.raw} call${ctx.raw === 1 ? '' : 's'}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: {
          display: true,
          text: 'Time after recipient stops speaking',
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#666',
        },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 10 },
          color: '#888',
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        title: {
          display: true,
          text: 'Number of calls',
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#666',
        },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  // Compute median/p90 latency for summary
  const sortedLatencies = [...latencies].sort((a, b) => a - b);
  const medianLatency =
    sortedLatencies.length > 0
      ? sortedLatencies[Math.floor(sortedLatencies.length * 0.5)]
      : null;
  const p90Latency =
    sortedLatencies.length > 0
      ? sortedLatencies[Math.floor(sortedLatencies.length * 0.9)]
      : null;

  return (
    <div className='ai-calls-view'>
      {/* Summary tiles */}
      <div className='outcomes-summary'>
        <div className='summary-tile primary'>
          <div className='tile-label'>Success Rate</div>
          <div className='tile-value'>{formatPct(totals.successRate)}</div>
          <div className='tile-sub'>
            {totals.successCount} successful of {totals.assessedCalls} assessed
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Connection Rate</div>
          <div className='tile-value'>{formatPct(totals.connectionRate)}</div>
          <div className='tile-sub'>
            {totals.connectedCalls} connected of {totals.totalCalls} total
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Total Calls</div>
          <div className='tile-value'>{totals.totalCalls}</div>
          <div className='tile-sub'>{totals.assessedCalls} assessed by AI</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Response Latency (P50)</div>
          <div className='tile-value'>
            {medianLatency != null
              ? `${(medianLatency / 1000).toFixed(2)}s`
              : '—'}
          </div>
          <div className='tile-sub'>
            P90:{' '}
            {p90Latency != null ? `${(p90Latency / 1000).toFixed(2)}s` : '—'}
            {' · '}
            {latencies.length} samples
          </div>
        </div>
      </div>

      {/* Stacked bar: outcomes per day */}
      <div className='chart-card wide outcomes-chart'>
        <Bar data={stackedData} options={stackedOptions} />
      </div>

      {/* Response latency histogram */}
      {latencies.length > 0 && (
        <div className='chart-card wide outcomes-chart'>
          <Bar data={histogramData} options={histogramOptions} />
        </div>
      )}

      {/* Assessment breakdown table */}
      {data.assessmentCategories.length > 0 && (
        <div className='outcomes-breakdown'>
          <div className='breakdown-title'>
            Detailed Assessment Categories ({totals.assessedCalls} assessed
            calls)
          </div>
          <table className='outcomes-table'>
            <thead>
              <tr>
                <th>Category</th>
                <th>Count</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {data.assessmentCategories.map((row) => (
                <tr key={row.category}>
                  <td>{CATEGORY_LABELS[row.category] ?? row.category}</td>
                  <td>{row.count}</td>
                  <td>
                    {totals.assessedCalls > 0
                      ? `${((row.count / totals.assessedCalls) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Email routing view ───────────────────────────────────────────────────

interface EmailRoutingViewProps {
  data: NonNullable<StatsResponse['emailRouting']>;
  /** Optional — older backends without the cost block omit this. */
  cost?: StatsResponse['emailRoutingCost'];
}

/**
 * Friendlier display labels for the server-emitted bucket names.
 *
 * The server emits canonical bucket strings like `'subscription_match (user)'`
 * that match the data model 1:1. The pie chart legend reads better with
 * sentence-case labels, so we map known buckets here. Unknown buckets
 * (future code paths) fall through to the raw bucket name so analytics
 * keep working even when the FE is out of date.
 */
const EMAIL_ROUTING_BUCKET_LABELS: Record<string, string> = {
  filtered: 'Filtered (spam / promo / receipt)',
  out_of_credits: 'Out of credits',
  'subscription_match (user)': 'Subscription match — user inbox',
  'subscription_match (agent)': 'Subscription match — agent inbox',
  no_match_drop: 'No match — silent drop',
  'no_match_drop (user)': 'No match — silent drop (user)',
  'no_match_drop (agent)': 'No match — silent drop (agent)',
  auto_draft_queued: 'Auto-draft — reply drafted for review',
  fallback_routed: 'Agent fallback — routed to thread',
  fallback_artifact: 'Agent fallback — artifact created',
  uncategorized: 'Uncategorized',
};

const labelForBucket = (bucket: string): string =>
  EMAIL_ROUTING_BUCKET_LABELS[bucket] ?? bucket;

/**
 * Pie-chart triplet showing the share of inbound emails by routing
 * outcome over the past month / week / day. Mirrors the trigger-cost
 * pie layout in `CostsView` so the two tabs feel familiar.
 */
const EmailRoutingView: FC<EmailRoutingViewProps> = ({ data, cost }) => {
  const windows = [
    { label: 'Past Month', slices: data.month },
    { label: 'Past Week', slices: data.week },
    { label: 'Past Day', slices: data.day },
  ] as const;

  // Aggregate totals for the summary tiles.
  const monthTotal = data.month.reduce((s, r) => s + r.count, 0);
  const weekTotal = data.week.reduce((s, r) => s + r.count, 0);
  const dayTotal = data.day.reduce((s, r) => s + r.count, 0);

  // ── handleEmail cost: today vs same-time avg comparison ──────────────
  // Delta is calculated against the prior-days average so positive
  // values mean "today is more expensive than usual by this point".
  // We deliberately leave the delta uncoloured when prior data is too
  // thin to be meaningful (no prior days, or zero average) — a fake
  // "+infinity%" is worse than just showing the raw numbers.
  const today = cost?.todayVsAverage.today ?? 0;
  const priorAvg = cost?.todayVsAverage.priorAverage ?? 0;
  const priorDays = cost?.todayVsAverage.priorDays ?? 0;
  const hasComparable = priorDays > 0 && priorAvg > 0;
  const deltaPct = hasComparable ? ((today - priorAvg) / priorAvg) * 100 : null;
  const deltaColor =
    deltaPct === null
      ? '#888'
      : deltaPct > 25
        ? '#dc2626'
        : deltaPct > 10
          ? '#f59e0b'
          : deltaPct < -10
            ? '#16a34a'
            : '#555';

  // Daily cost line chart data — last 30 days, today is the rightmost
  // (partial) point. Treat missing days as $0 (server already
  // zero-fills, but defensively coalesce here too).
  const daily = cost?.daily ?? [];

  return (
    <div className='costs-view'>
      <div className='costs-summary'>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (30d)</div>
          <div className='tile-value'>{monthTotal.toLocaleString()}</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (7d)</div>
          <div className='tile-value'>{weekTotal.toLocaleString()}</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (1d)</div>
          <div className='tile-value'>{dayTotal.toLocaleString()}</div>
        </div>
      </div>

      {cost && (
        <>
          {/* ── handleEmail cost: today vs same-time avg ──────────── */}
          <div className='costs-summary'>
            <div className='summary-tile'>
              <div className='tile-label'>handleEmail today (so far)</div>
              <div className='tile-value'>{formatUSD(today)}</div>
            </div>
            <div className='summary-tile'>
              <div className='tile-label'>
                Avg by this time ({priorDays}d prior)
              </div>
              <div className='tile-value'>{formatUSD(priorAvg)}</div>
            </div>
            <div className='summary-tile'>
              <div className='tile-label'>Δ vs same-time average</div>
              <div className='tile-value' style={{ color: deltaColor }}>
                {deltaPct === null
                  ? '—'
                  : `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`}
              </div>
            </div>
          </div>

          {/* ── handleEmail daily cost line chart (30 days) ─────── */}
          <div className='costs-chart-card wide'>
            <Line
              data={{
                labels: daily.map((d) => formatLabel(d.date)),
                datasets: [
                  {
                    label: 'handleEmail $/day',
                    data: daily.map((d) => d.cost),
                    borderColor: 'rgba(99, 102, 241, 1)',
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    fill: true,
                    tension: 0.25,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                ...lineChartOptions('handleEmail cost per day (last 30d)'),
                plugins: {
                  ...lineChartOptions('handleEmail cost per day (last 30d)')
                    .plugins,
                  tooltip: {
                    callbacks: {
                      label: (ctx: any) =>
                        `${ctx.dataset.label}: ${formatUSD(ctx.parsed.y as number)}`,
                    },
                  },
                },
                scales: {
                  ...lineChartOptions('handleEmail cost per day (last 30d)')
                    .scales,
                  y: {
                    ...lineChartOptions('handleEmail cost per day (last 30d)')
                      .scales.y,
                    ticks: {
                      ...lineChartOptions('handleEmail cost per day (last 30d)')
                        .scales.y.ticks,
                      callback: (v: any) => formatUSD(v),
                    },
                  },
                },
              }}
            />
          </div>
        </>
      )}

      <div className='costs-trigger-pies'>
        {windows.map(({ label, slices }) => {
          const total = slices.reduce((s, r) => s + r.count, 0);
          if (total === 0) {
            return (
              <div className='costs-chart-card pie-card' key={label}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#888',
                    fontFamily: 'Instrument Sans, sans-serif',
                  }}
                >
                  {label} — no emails
                </div>
              </div>
            );
          }
          return (
            <div className='costs-chart-card pie-card' key={label}>
              <Pie
                data={{
                  labels: slices.map((s) => labelForBucket(s.bucket)),
                  datasets: [
                    {
                      data: slices.map((s) => s.count),
                      backgroundColor: [
                        ...MODEL_COLORS,
                        'rgba(100, 116, 139, 0.75)',
                        'rgba(251, 146, 60, 0.75)',
                        'rgba(99, 102, 241, 0.75)',
                      ],
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: `${label} — ${total.toLocaleString()} emails`,
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 14,
                        weight: 600 as const,
                      },
                      color: '#222',
                      padding: { bottom: 8 },
                    },
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        font: {
                          family: 'Instrument Sans, sans-serif',
                          size: 10,
                        },
                        boxWidth: 10,
                        padding: 6,
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx: any) => {
                          const val = ctx.raw as number;
                          const pct =
                            total > 0 ? ((val / total) * 100).toFixed(1) : '0';
                          return `${ctx.label}: ${val.toLocaleString()} (${pct}%)`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Browser & MFA view ───────────────────────────────────────────────────

const MFA_SOURCE_COLORS: Record<string, string> = {
  agent_tool: 'rgba(99, 102, 241, 0.8)',
  browser_intervention: 'rgba(251, 146, 60, 0.8)',
  human_task: 'rgba(168, 85, 247, 0.8)',
};

const MFA_OUTCOME_COLORS: Record<string, string> = {
  submitted: 'rgba(34, 197, 94, 0.8)',
  expired: 'rgba(239, 68, 68, 0.8)',
  cancelled: 'rgba(156, 163, 175, 0.8)',
};

const PIE_PALETTE = [
  'rgba(99, 102, 241, 0.8)',
  'rgba(34, 197, 94, 0.8)',
  'rgba(251, 146, 60, 0.8)',
  'rgba(239, 68, 68, 0.8)',
  'rgba(168, 85, 247, 0.8)',
  'rgba(14, 165, 233, 0.8)',
  'rgba(156, 163, 175, 0.8)',
];

const formatMs = (ms: number | null) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const BrowserMfaView: FC<{ data: BrowserSessionsResponse }> = ({ data }) => {
  const labels = data.days;

  return (
    <div className='browser-mfa-view'>
      {/* ─── Section 1: Session Volume ───────────────────────────────── */}
      <section className='browser-mfa-section-block'>
        <h3>Browser Sessions</h3>
        <div className='stat-badges-row'>
          <span className='stat-badge'>
            Total: <strong>{data.totalSessions}</strong>
          </span>
        </div>
        <div className='chart-card wide'>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Sessions',
                  data: data.sessionsPerDay,
                  backgroundColor: 'rgba(99, 102, 241, 0.7)',
                  borderRadius: 3,
                  barPercentage: 0.8,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                title: {
                  display: true,
                  text: 'Browser Sessions per Day',
                  font: {
                    family: 'Instrument Sans, sans-serif',
                    size: 14,
                    weight: 600 as const,
                  },
                  color: '#222',
                },
                legend: { display: false },
              },
              scales: {
                x: { ticks: { font: { size: 10 } } },
                y: { beginAtZero: true, ticks: { stepSize: 1 } },
              },
            }}
          />
        </div>
        {data.sessionOutcomes.length > 0 ? (
          <div className='chart-card'>
            <Pie
              data={{
                labels: data.sessionOutcomes.map((o) => o.outcome),
                datasets: [
                  {
                    data: data.sessionOutcomes.map((o) => o.count),
                    backgroundColor: PIE_PALETTE,
                    borderWidth: 1,
                    borderColor: '#fff',
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'Session Outcomes',
                    font: {
                      family: 'Instrument Sans, sans-serif',
                      size: 14,
                      weight: 600 as const,
                    },
                    color: '#222',
                  },
                  legend: { position: 'bottom' as const },
                },
              }}
            />
          </div>
        ) : (
          <p className='no-data-placeholder'>No session outcome data yet.</p>
        )}
      </section>

      {/* ─── Section 2: Cookie Reuse ─────────────────────────────────── */}
      <section className='browser-mfa-section-block'>
        <h3>Cookie Reuse</h3>
        <p className='section-description'>
          Sessions where previously-stored cookies allowed the agent to skip
          login on a genuinely useful domain (e.g. LinkedIn, banking portal).
        </p>
        <div className='stat-badges-row'>
          <span className='stat-badge'>
            Reuse rate:{' '}
            <strong>
              {data.cookieReuse.reuseRate != null
                ? `${data.cookieReuse.reuseRate}%`
                : '—'}
            </strong>
          </span>
          <span className='stat-badge'>
            {data.cookieReuse.reusedCount} /{' '}
            {data.cookieReuse.totalEndedSessions} sessions
          </span>
        </div>
        {data.cookieReuse.topDomains.length > 0 && (
          <div className='top-domains-list'>
            <h4>Top reuse domains</h4>
            <ul>
              {data.cookieReuse.topDomains.map((d) => (
                <li key={d.domain}>
                  <span className='domain-name'>{d.domain}</span>
                  <span className='domain-count'>{d.count}&times;</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ─── Section 3: Interventions ────────────────────────────────── */}
      <section className='browser-mfa-section-block'>
        <h3>Browser Interventions</h3>
        <div className='stat-badges-row'>
          <span className='stat-badge'>
            Sessions needing intervention:{' '}
            <strong>
              {data.interventions.interventionRate != null
                ? `${data.interventions.interventionRate}%`
                : '—'}
            </strong>
          </span>
          <span className='stat-badge'>
            {data.interventions.sessionsWithIntervention} /{' '}
            {data.interventions.totalSessions} sessions
          </span>
          <span className='stat-badge'>
            Total interventions:{' '}
            <strong>{data.interventions.totalInterventions}</strong>
          </span>
        </div>
        {data.interventions.outcomes.length > 0 && (
          <div className='chart-card'>
            <Pie
              data={{
                labels: data.interventions.outcomes.map(
                  (o) => o.outcome || 'unknown',
                ),
                datasets: [
                  {
                    data: data.interventions.outcomes.map((o) => o.count),
                    backgroundColor: PIE_PALETTE,
                    borderWidth: 1,
                    borderColor: '#fff',
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'Intervention Outcomes',
                    font: {
                      family: 'Instrument Sans, sans-serif',
                      size: 14,
                      weight: 600 as const,
                    },
                    color: '#222',
                  },
                  legend: { position: 'bottom' as const },
                },
              }}
            />
          </div>
        )}
      </section>

      {/* ─── Section 4: MFA / 3DS ────────────────────────────────────── */}
      <section className='browser-mfa-section-block'>
        <h3>MFA / 3DS Requests</h3>
        <div className='stat-badges-row'>
          <span className='stat-badge'>
            Total: <strong>{data.mfa.total}</strong>
          </span>
          <span className='stat-badge'>
            Avg response:{' '}
            <strong>{formatMs(data.mfa.responseTime.avgMs)}</strong>
          </span>
          <span className='stat-badge'>
            Median response:{' '}
            <strong>{formatMs(data.mfa.responseTime.medianMs)}</strong>
          </span>
        </div>

        <div className='chart-card wide'>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'MFA Requests',
                  data: data.mfa.perDay,
                  backgroundColor: 'rgba(168, 85, 247, 0.7)',
                  borderRadius: 3,
                  barPercentage: 0.8,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                title: {
                  display: true,
                  text: 'MFA Requests per Day',
                  font: {
                    family: 'Instrument Sans, sans-serif',
                    size: 14,
                    weight: 600 as const,
                  },
                  color: '#222',
                },
                legend: { display: false },
              },
              scales: {
                x: { ticks: { font: { size: 10 } } },
                y: { beginAtZero: true, ticks: { stepSize: 1 } },
              },
            }}
          />
        </div>

        <div className='charts-grid pie-row'>
          {/* By source */}
          <div className='chart-card'>
            {data.mfa.bySource.length > 0 ? (
              <Pie
                data={{
                  labels: data.mfa.bySource.map((s) =>
                    s.source.replace(/_/g, ' '),
                  ),
                  datasets: [
                    {
                      data: data.mfa.bySource.map((s) => s.count),
                      backgroundColor: data.mfa.bySource.map(
                        (s) =>
                          MFA_SOURCE_COLORS[s.source] ||
                          'rgba(156, 163, 175, 0.8)',
                      ),
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'By Source',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { position: 'bottom' as const },
                  },
                }}
              />
            ) : (
              <p className='no-data-placeholder'>No MFA source data yet.</p>
            )}
          </div>

          {/* By outcome */}
          <div className='chart-card'>
            {data.mfa.byOutcome.length > 0 ? (
              <Pie
                data={{
                  labels: data.mfa.byOutcome.map((o) => o.outcome || 'pending'),
                  datasets: [
                    {
                      data: data.mfa.byOutcome.map((o) => o.count),
                      backgroundColor: data.mfa.byOutcome.map(
                        (o) =>
                          MFA_OUTCOME_COLORS[o.outcome] ||
                          'rgba(156, 163, 175, 0.8)',
                      ),
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'By Outcome',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { position: 'bottom' as const },
                  },
                }}
              />
            ) : (
              <p className='no-data-placeholder'>No MFA outcome data yet.</p>
            )}
          </div>

          {/* By type */}
          <div className='chart-card'>
            {data.mfa.byType.length > 0 ? (
              <Pie
                data={{
                  labels: data.mfa.byType.map((t) =>
                    t.tfaType.replace(/_/g, ' '),
                  ),
                  datasets: [
                    {
                      data: data.mfa.byType.map((t) => t.count),
                      backgroundColor: PIE_PALETTE,
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'By Type (email code, SMS, app push, etc.)',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { position: 'bottom' as const },
                  },
                }}
              />
            ) : (
              <p className='no-data-placeholder'>No MFA type data yet.</p>
            )}
          </div>
        </div>
      </section>
      <PlatformStatsCaptchaSolver
        stats={data.captchaSolver}
        proxy={data.proxy}
        days={labels}
      />
    </div>
  );
};

export default PlatformStats;
